/* MGC App Frontend Logic - Handles OTP Flow, Dashboard sync, Clipboard and Sharing */

document.addEventListener('DOMContentLoaded', function () {
  initializeGlobalReferral();
  initializeTabs();
  initializeReferralPrefill();
  initializeOTPInputs();
  initializeAuthForm();
  initializeDashboard();
});

const PROXY_BASE = '/apps/mgc';

function escapeHtml(str) {
  if (!str) return '';
  return str
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");
}

async function fetchJsonHelper(url, options = {}) {
  try {
    const response = await fetch(url, options);
    const text = await response.text();

    let data;
    try {
      data = JSON.parse(text);
    } catch (parseErr) {
      if (text.includes('<!DOCTYPE html>') || text.includes('<html') || response.status === 404) {
        throw new Error(`Shopify App Proxy routing error. Please ensure your Shopify App is installed on the store, the App Proxy is configured correctly in Shopify Partners, and your backend server is running (Status: ${response.status}).`);
      }
      throw new Error(`Invalid server response (Status ${response.status}): ${text.substring(0, 100)}`);
    }

    if (!response.ok) {
      throw new Error(data.error || data.message || `Request failed with status ${response.status}`);
    }

    if (data && data.success === false) {
      throw new Error(data.error || data.message || 'Operation failed.');
    }

    return data;
  } catch (err) {
    console.error('[MGC Fetch Error]', err);
    throw err;
  }
}

// Helper to fetch and construct checkout pre-fill parameters
async function getCustomerCheckoutParams() {
  let email = localStorage.getItem('mgc_customer_email') || '';
  let phone = localStorage.getItem('mgc_customer_phone') || '';
  let name = localStorage.getItem('mgc_customer_name') || '';
  let city = localStorage.getItem('mgc_customer_city') || '';

  const token = localStorage.getItem('mgc_session_token');
  if (token && (!email && !phone)) {
    try {
      const result = await fetchJsonHelper(`${PROXY_BASE}/dashboard`, {
        method: 'GET',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const customerData = result.customer || result;
      email = customerData.email || '';
      phone = customerData.phone || '';
      name = customerData.name || '';
      city = customerData.city || '';

      localStorage.setItem('mgc_customer_email', email);
      localStorage.setItem('mgc_customer_phone', phone);
      localStorage.setItem('mgc_customer_name', name);
      localStorage.setItem('mgc_customer_city', city);
    } catch (e) {
      console.error('Failed to pre-fetch customer details for checkout:', e);
    }
  }

  const params = [];
  if (email) params.push(`checkout[email]=${encodeURIComponent(email)}`);
  if (phone) params.push(`checkout[shipping_address][phone]=${encodeURIComponent(phone)}`);

  if (name) {
    const nameParts = name.trim().split(/\s+/);
    const firstName = nameParts[0] || '';
    const lastName = nameParts.slice(1).join(' ') || '';
    if (firstName) params.push(`checkout[shipping_address][first_name]=${encodeURIComponent(firstName)}`);
    if (lastName) params.push(`checkout[shipping_address][last_name]=${encodeURIComponent(lastName)}`);
  }

  if (city && city !== 'N/A') {
    params.push(`checkout[shipping_address][city]=${encodeURIComponent(city)}`);
  }
  params.push(`checkout[shipping_address][country]=India`);

  return params.join('&');
}

// Debug logger helper
function mgcLog(...args) {
  if (localStorage.getItem('mgc_debug') === '1') {
    console.log('[MGC Debug]', ...args);
  }
}

function mgcWarn(...args) {
  if (localStorage.getItem('mgc_debug') === '1') {
    console.warn('[MGC Warning]', ...args);
  }
}

/**
 * Handle switching between standard Shopify login form and the MGC OTP login form
 */
function initializeTabs() {
  const tabs = document.querySelectorAll('.mgc-tab-btn');

  // Hide standard Shopify login form if custom OTP form is present on the page
  const hasCustomAuth = document.getElementById('mgc-auth-form');
  if (hasCustomAuth) {
    const nativeLogin = document.querySelector('.customer-login');
    if (nativeLogin) {
      nativeLogin.style.display = 'none';
    }
  }

  if (tabs.length === 0) return;

  tabs.forEach(tab => {
    tab.addEventListener('click', function () {
      tabs.forEach(t => t.classList.remove('active'));
      this.classList.add('active');

      const targetId = this.getAttribute('data-target');
      mgcLog('Tab changed to:', targetId);

      // Hide all auth containers
      document.querySelectorAll('.mgc-auth-view').forEach(view => {
        view.style.display = 'none';
      });

      // Show selected container
      const targetView = document.getElementById(targetId);
      if (targetView) targetView.style.display = 'block';

      // Hide or show native customer forms if we need to toggle them
      const nativeLogin = document.querySelector('.customer-login');
      if (nativeLogin) {
        if (targetId === 'mgc-otp-login-view') {
          nativeLogin.style.display = 'none';
        } else {
          nativeLogin.style.display = 'block';
        }
      }
    });
  });
}

/**
 * Pre-fill referral code from localStorage/cookie if present
 */
function initializeReferralPrefill() {
  const referralInput = document.getElementById('mgc-signup-referral-code') || document.getElementById('mgc-referral-code');
  const referralMsg = document.getElementById('mgc-signup-referral-msg') || document.getElementById('mgc-referral-msg');
  if (referralInput) {
    const storedRef = localStorage.getItem('mgckart_referral_code') ||
      localStorage.getItem('mgc_referral_code') ||
      getCookie('mgckart_referral_code') ||
      getCookie('mgc_referral_code');
    if (storedRef) {
      const cleanRef = storedRef.trim().toUpperCase().replace(/[^A-Z0-9\-_]/g, '').substring(0, 50);
      if (cleanRef) {
        referralInput.value = cleanRef;
        referralInput.disabled = true;

        if (referralMsg) {
          referralMsg.textContent = `Referral code applied: ${cleanRef}`;
          referralMsg.style.display = 'block';
        }

        // Append a hidden input so the form submission still gets the value
        const form = document.getElementById('mgc-auth-form') || document.getElementById('mgc-send-otp-form');
        if (form) {
          let hiddenInput = form.querySelector('input[name="referralCode"]');
          if (!hiddenInput) {
            hiddenInput = document.createElement('input');
            hiddenInput.type = 'hidden';
            hiddenInput.name = 'referralCode';
            form.appendChild(hiddenInput);
          }
          hiddenInput.value = cleanRef;
        }
      }
    }
  }
}

/**
 * Handle auto-focus jump in 6-digit OTP code input boxes
 */
function initializeOTPInputs() {
  const otpFields = document.querySelectorAll('.mgc-otp-char');
  if (otpFields.length === 0) return;

  otpFields.forEach((field, index) => {
    // Only allow numbers
    field.addEventListener('input', function () {
      this.value = this.value.replace(/[^0-9]/g, '');

      if (this.value.length >= 1) {
        // Jump to next input
        if (index < otpFields.length - 1) {
          otpFields[index + 1].focus();
        }
      }
      combineOTP();
    });

    // Support backspace reverse jumping
    field.addEventListener('keydown', function (e) {
      if (e.key === 'Backspace') {
        if (this.value.length === 0 && index > 0) {
          otpFields[index - 1].focus();
          otpFields[index - 1].value = '';
          combineOTP();
        }
      }
    });

    // Support paste of full 6-digit OTP
    field.addEventListener('paste', function (e) {
      e.preventDefault();
      const pasteData = (e.clipboardData || window.clipboardData).getData('text');
      const cleanDigits = pasteData.replace(/[^0-9]/g, '').slice(0, 6);

      if (cleanDigits.length > 0) {
        let currentIdx = index;
        for (let i = 0; i < cleanDigits.length && currentIdx < otpFields.length; i++) {
          otpFields[currentIdx].value = cleanDigits[i];
          currentIdx++;
        }
        // Focus the next empty field or the last field
        const focusIdx = Math.min(index + cleanDigits.length, otpFields.length - 1);
        otpFields[focusIdx].focus();
        combineOTP();
      }
    });
  });
}

function combineOTP() {
  const otpFields = document.querySelectorAll('.mgc-otp-char');
  let code = '';
  otpFields.forEach(field => {
    code += field.value;
  });
  const hiddenOTP = document.getElementById('mgc-full-otp');
  if (hiddenOTP) {
    hiddenOTP.value = code;
  }
}

/**
 * Handle OTP Auth forms (Submit name/phone/email, verify OTP)
 */
function initializeAuthForm() {
  const authForm = document.getElementById('mgc-auth-form');
  if (!authForm) return;

  // Auto-redirect if session is active
  const token = localStorage.getItem('mgc_session_token');
  if (token) {
    mgcLog('Active session found. Redirecting to dashboard.');
    let finalUrl = '/pages/mgc-dashboard';
    try {
      const currentUrl = new URL(window.location.href);
      const targetUrlObj = new URL(finalUrl, window.location.origin);
      const paramsToPreserve = ['preview_theme_id', '_preview_theme_id', '_fd', '_iframe'];
      paramsToPreserve.forEach(param => {
        if (currentUrl.searchParams.has(param)) {
          targetUrlObj.searchParams.set(param, currentUrl.searchParams.get(param));
        }
      });
      finalUrl = targetUrlObj.pathname + targetUrlObj.search;
    } catch (e) {
      console.error('Failed to preserve preview parameters:', e);
    }
    window.location.href = finalUrl;
    return;
  }

  const authAlert = document.getElementById('mgc-auth-alert');

  // Login DOM Elements
  const loginFieldsContainer = document.getElementById('mgc-login-fields-container');
  const loginIdentifierInput = document.getElementById('mgc-login-identifier');
  const loginSendOtpBtn = document.getElementById('mgc-login-send-otp-btn');
  const loginOtpRow = document.getElementById('mgc-login-otp-row');
  const loginOtpInput = document.getElementById('mgc-login-otp-code');
  const loginResendBtn = document.getElementById('mgc-login-resend-btn');

  // Signup DOM Elements
  const signupFieldsContainer = document.getElementById('mgc-signup-fields-container');
  const signupPhoneInput = document.getElementById('mgc-signup-phone');
  const signupSendMobileOtpBtn = document.getElementById('mgc-signup-send-mobile-otp-btn');
  const signupMobileOtpRow = document.getElementById('mgc-signup-mobile-otp-row');
  const signupMobileOtpInput = document.getElementById('mgc-signup-mobile-otp');
  const signupMobileResendBtn = document.getElementById('mgc-signup-mobile-resend-btn');

  const signupEmailInput = document.getElementById('mgc-signup-email');
  const signupSendEmailOtpBtn = document.getElementById('mgc-signup-send-email-otp-btn');
  const signupEmailOtpRow = document.getElementById('mgc-signup-email-otp-row');
  const signupEmailOtpInput = document.getElementById('mgc-signup-email-otp');
  const signupEmailResendBtn = document.getElementById('mgc-signup-email-resend-btn');

  const signupSendUnifiedOtpBtn = document.getElementById('mgc-signup-send-unified-otp-btn');
  const signupMobileOtpLabel = document.querySelector('#mgc-signup-mobile-otp-row .mgc-field-label');

  const signupNameInput = document.getElementById('mgc-signup-name');
  const signupCityInput = document.getElementById('mgc-signup-city');
  const signupReferralInput = document.getElementById('mgc-signup-referral-code');

  const submitAuthBtn = document.getElementById('mgc-submit-auth-btn');
  const submitAuthBtnText = document.getElementById('mgc-submit-btn-text');

  // UI toggles and elements
  const authTitle = document.getElementById('mgc-auth-title');
  const authSubtitle = document.getElementById('mgc-auth-subtitle');
  const toggleModeBtn = document.getElementById('mgc-toggle-mode-btn');
  const togglePrefix = document.getElementById('mgc-toggle-prefix');
  const emailConsentInput = document.getElementById('mgc-email-consent');
  const smsConsentInput = document.getElementById('mgc-sms-consent');
  const signupLegalConsentCheckbox = document.getElementById('mgc-legal-consent-checkbox');

  if (signupLegalConsentCheckbox) {
    signupLegalConsentCheckbox.addEventListener('change', function () {
      const isChecked = this.checked;
      if (signupSendMobileOtpBtn) signupSendMobileOtpBtn.disabled = !isChecked;
      if (signupSendEmailOtpBtn) signupSendEmailOtpBtn.disabled = !isChecked;
      if (signupSendUnifiedOtpBtn) signupSendUnifiedOtpBtn.disabled = !isChecked;
      if (submitAuthBtn && formMode === 'signup') {
        submitAuthBtn.disabled = !isChecked;
      }
    });
  }

  let formMode = 'login'; // 'login' or 'signup'
  let loginOtpSent = false;
  let signupMobileOtpSent = false;
  let signupEmailOtpSent = false;
  let otpCodeMode = 'different';

  fetchJsonHelper(`${PROXY_BASE}/otp-config`)
    .then(res => {
      if (res && res.success && res.settings) {
        otpCodeMode = res.settings.otpCodeMode || 'different';
        applyDynamicOtpLayout();
      }
    })
    .catch(err => {
      console.error('Failed to load OTP configuration:', err);
    });

  function applyDynamicOtpLayout() {
    if (formMode !== 'signup') return;

    if (otpCodeMode === 'same') {
      if (signupSendMobileOtpBtn) signupSendMobileOtpBtn.style.display = 'none';
      if (signupSendEmailOtpBtn) signupSendEmailOtpBtn.style.display = 'none';
      if (signupSendUnifiedOtpBtn) signupSendUnifiedOtpBtn.style.display = 'block';
      if (signupMobileOtpLabel) signupMobileOtpLabel.textContent = 'Enter OTP';

      // Move mobile OTP row to be below the email group for a cleaner unified layout
      const emailGroup = document.getElementById('mgc-signup-email')?.closest('.mgc-field-group');
      const mobileOtpRow = document.getElementById('mgc-signup-mobile-otp-row');
      if (emailGroup && mobileOtpRow) {
        emailGroup.parentNode.insertBefore(mobileOtpRow, emailGroup.nextSibling);
      }
    } else {
      if (signupSendMobileOtpBtn) signupSendMobileOtpBtn.style.display = 'block';
      if (signupSendEmailOtpBtn) signupSendEmailOtpBtn.style.display = 'block';
      if (signupSendUnifiedOtpBtn) signupSendUnifiedOtpBtn.style.display = 'none';
      if (signupMobileOtpLabel) signupMobileOtpLabel.textContent = 'Enter Mobile OTP';

      // Move it back inside the mobile group container if switched to different
      const mobileGroup = document.getElementById('mgc-signup-phone')?.closest('.mgc-phone-select-wrapper');
      const mobileOtpRow = document.getElementById('mgc-signup-mobile-otp-row');
      if (mobileGroup && mobileOtpRow) {
        mobileGroup.parentNode.appendChild(mobileOtpRow);
      }
    }
  }

  function showAlert(msg, type = 'success') {
    if (!authAlert) return;
    const titleEl = document.getElementById('mgc-alert-title');
    const descEl = document.getElementById('mgc-alert-desc');
    const successIcon = document.getElementById('mgc-alert-success-icon');
    const errorIcon = document.getElementById('mgc-alert-error-icon');

    if (type === 'success' || type === 'warning') {
      authAlert.className = 'mgc-alert-banner';
      if (titleEl) titleEl.textContent = type === 'warning' ? 'Warning' : 'Welcome back!';
      if (descEl) descEl.textContent = msg;
      if (successIcon) successIcon.style.display = 'block';
      if (errorIcon) errorIcon.style.display = 'none';
    } else {
      authAlert.className = 'mgc-alert-banner mgc-alert-error-banner';
      if (titleEl) titleEl.textContent = 'Error';
      if (descEl) descEl.textContent = msg;
      if (successIcon) successIcon.style.display = 'none';
      if (errorIcon) errorIcon.style.display = 'block';
    }
    authAlert.style.display = 'flex';
  }

  function hideAlert() {
    if (authAlert) authAlert.style.display = 'none';
  }

  function updateProgressStep(step) {
    const steps = [1, 2, 3];
    steps.forEach(s => {
      const circle = document.getElementById(`mgc-step-${s}-circle`);
      const label = document.getElementById(`mgc-step-${s}-label`);
      if (circle && label) {
        if (s <= step) {
          circle.className = 'mgc-step-circle active';
          label.className = 'mgc-step-label active';
        } else {
          circle.className = 'mgc-step-circle inactive';
          label.className = 'mgc-step-label inactive';
        }
      }
    });
  }

  function resetFormInputs() {
    loginOtpSent = false;
    signupMobileOtpSent = false;
    signupEmailOtpSent = false;

    // Reset login inputs
    if (loginIdentifierInput) {
      loginIdentifierInput.disabled = false;
      loginIdentifierInput.value = '';
    }
    if (loginOtpInput) loginOtpInput.value = '';
    if (loginOtpRow) loginOtpRow.style.display = 'none';
    if (loginSendOtpBtn) {
      loginSendOtpBtn.disabled = false;
      loginSendOtpBtn.innerText = 'Get OTP';
    }
    if (loginResendBtn) {
      loginResendBtn.disabled = false;
      loginResendBtn.innerText = 'Resend now';
    }

    // Reset signup inputs
    if (signupLegalConsentCheckbox) {
      signupLegalConsentCheckbox.checked = false;
    }
    const isConsentChecked = signupLegalConsentCheckbox ? signupLegalConsentCheckbox.checked : false;

    if (signupPhoneInput) {
      signupPhoneInput.disabled = false;
      signupPhoneInput.value = '';
    }
    if (signupMobileOtpInput) signupMobileOtpInput.value = '';
    if (signupMobileOtpRow) signupMobileOtpRow.style.display = 'none';
    if (signupSendMobileOtpBtn) {
      signupSendMobileOtpBtn.disabled = !isConsentChecked;
      signupSendMobileOtpBtn.innerText = 'Get OTP';
    }
    if (signupMobileResendBtn) {
      signupMobileResendBtn.disabled = !isConsentChecked;
      signupMobileResendBtn.innerText = 'Resend now';
    }

    if (signupEmailInput) {
      signupEmailInput.disabled = false;
      signupEmailInput.value = '';
    }
    if (signupEmailOtpInput) signupEmailOtpInput.value = '';
    if (signupEmailOtpRow) signupEmailOtpRow.style.display = 'none';
    if (signupSendEmailOtpBtn) {
      signupSendEmailOtpBtn.disabled = !isConsentChecked;
      signupSendEmailOtpBtn.innerText = 'Get OTP';
    }
    if (signupEmailResendBtn) {
      signupEmailResendBtn.disabled = !isConsentChecked;
      signupEmailResendBtn.innerText = 'Resend now';
    }

    if (signupSendUnifiedOtpBtn) {
      signupSendUnifiedOtpBtn.disabled = !isConsentChecked;
    }

    if (signupNameInput) signupNameInput.value = '';
    if (signupCityInput) signupCityInput.value = '';
    if (signupReferralInput) {
      const storedRef = localStorage.getItem('mgckart_referral_code') ||
        localStorage.getItem('mgc_referral_code') ||
        getCookie('mgckart_referral_code') ||
        getCookie('mgc_referral_code');
      if (storedRef) {
        signupReferralInput.value = storedRef.trim().toUpperCase().replace(/[^A-Z0-9\-_]/g, '').substring(0, 50);
      } else {
        signupReferralInput.value = '';
      }
    }

    if (submitAuthBtn) {
      submitAuthBtn.style.display = 'none';
      if (formMode === 'signup') {
        submitAuthBtn.disabled = !isConsentChecked;
      }
    }
  }

  function setFormMode(mode) {
    formMode = mode;
    hideAlert();
    const signupProgress = document.getElementById('mgc-signup-progress');
    const authContainer = document.querySelector('.mgc-registration-card');

    if (mode === 'signup') {
      if (authTitle) authTitle.textContent = authContainer?.dataset?.titleSignup || 'Create Account';
      if (authSubtitle) authSubtitle.textContent = authContainer?.dataset?.subtitleSignup || 'Join MGCKART for a premium shopping experience.';
      if (togglePrefix) togglePrefix.textContent = authContainer?.dataset?.toggleLoginPrefix || 'Already have an account? ';
      if (toggleModeBtn) toggleModeBtn.textContent = authContainer?.dataset?.toggleLoginBtn || 'Sign In';
      if (signupProgress) signupProgress.style.display = 'flex';
      updateProgressStep(1);

      if (loginFieldsContainer) loginFieldsContainer.style.display = 'none';
      if (signupFieldsContainer) signupFieldsContainer.style.display = 'block';

      if (signupNameInput) signupNameInput.setAttribute('required', 'required');
      if (signupCityInput) signupCityInput.setAttribute('required', 'required');
      if (signupPhoneInput) signupPhoneInput.setAttribute('required', 'required');
      if (signupEmailInput) signupEmailInput.removeAttribute('required');

      resetFormInputs();
      applyDynamicOtpLayout();
      if (submitAuthBtnText) {
        submitAuthBtnText.textContent = authContainer?.dataset?.titleSignup || 'Create Account';
      }
    } else {
      if (authTitle) authTitle.textContent = authContainer?.dataset?.titleLogin || 'Login';
      if (authSubtitle) authSubtitle.textContent = authContainer?.dataset?.subtitleLogin || 'EEnter your email or WhatsApp number to receive an OTP.';
      if (togglePrefix) togglePrefix.textContent = authContainer?.dataset?.toggleSignupPrefix || 'New to MGCKART? ';
      if (toggleModeBtn) toggleModeBtn.textContent = authContainer?.dataset?.toggleSignupBtn || 'Create account';
      if (signupProgress) signupProgress.style.display = 'none';

      if (loginFieldsContainer) loginFieldsContainer.style.display = 'block';
      if (signupFieldsContainer) signupFieldsContainer.style.display = 'none';

      if (signupNameInput) signupNameInput.removeAttribute('required');
      if (signupCityInput) signupCityInput.removeAttribute('required');
      if (signupPhoneInput) signupPhoneInput.removeAttribute('required');
      if (signupEmailInput) signupEmailInput.removeAttribute('required');

      resetFormInputs();
      if (submitAuthBtnText) {
        submitAuthBtnText.textContent = 'Complete Login';
      }
    }
  }

  if (toggleModeBtn) {
    toggleModeBtn.addEventListener('click', function (e) {
      e.preventDefault();
      if (formMode === 'login') {
        setFormMode('signup');
      } else {
        setFormMode('login');
      }
    });
  }

  function startCooldown(btn, seconds = 60, altBtn = null) {
    let remaining = seconds;
    const originalText = btn.innerText;
    const altOriginalText = altBtn ? altBtn.innerText : '';

    btn.disabled = true;
    btn.style.pointerEvents = 'none';
    btn.style.opacity = '0.6';
    btn.innerText = `Resend (${remaining}s)`;

    if (altBtn) {
      altBtn.disabled = true;
      altBtn.style.pointerEvents = 'none';
      altBtn.style.opacity = '0.6';
      altBtn.innerText = `Resend (${remaining}s)`;
    }

    const interval = setInterval(() => {
      remaining--;
      btn.innerText = `Resend (${remaining}s)`;
      if (altBtn) altBtn.innerText = `Resend (${remaining}s)`;

      if (remaining <= 0) {
        clearInterval(interval);
        btn.disabled = false;
        btn.style.pointerEvents = 'auto';
        btn.style.opacity = '1';
        btn.innerText = originalText;

        if (altBtn) {
          altBtn.disabled = false;
          altBtn.style.pointerEvents = 'auto';
          altBtn.style.opacity = '1';
          altBtn.innerText = altOriginalText;
        }
      }
    }, 1000);
  }

  // ================= RESEND BUTTON HANDLERS =================
  if (loginResendBtn && loginSendOtpBtn) {
    loginResendBtn.addEventListener('click', function (e) {
      e.preventDefault();
      if (loginIdentifierInput) loginIdentifierInput.disabled = false;
      loginSendOtpBtn.click();
    });
  }
  if (signupMobileResendBtn) {
    signupMobileResendBtn.addEventListener('click', function (e) {
      e.preventDefault();
      if (signupPhoneInput) signupPhoneInput.disabled = false;
      if (signupEmailInput) signupEmailInput.disabled = false;
      if (otpCodeMode === 'same') {
        if (signupSendUnifiedOtpBtn) signupSendUnifiedOtpBtn.click();
      } else {
        if (signupSendMobileOtpBtn) signupSendMobileOtpBtn.click();
      }
    });
  }
  if (signupEmailResendBtn && signupSendEmailOtpBtn) {
    signupEmailResendBtn.addEventListener('click', function (e) {
      e.preventDefault();
      if (signupEmailInput) signupEmailInput.disabled = false;
      signupSendEmailOtpBtn.click();
    });
  }

  // ================= LOGIN SEND OTP =================
  if (loginSendOtpBtn && loginIdentifierInput) {
    loginSendOtpBtn.addEventListener('click', async function (e) {
      e.preventDefault();
      hideAlert();

      let val = loginIdentifierInput.value.trim();
      if (!val) {
        showAlert('Please enter your email or mobile number.', 'error');
        return;
      }

      const isEmail = val.includes('@');
      if (!isEmail) {
        // Prepend country code +91 if 10-digit number is provided
        if (/^[0-9]{10}$/.test(val)) {
          val = '+91' + val;
          loginIdentifierInput.value = val;
        }

        if (!/^\+?[0-9]{10,15}$/.test(val)) {
          showAlert('Please enter a valid mobile number.', 'error');
          return;
        }
      } else {
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val)) {
          showAlert('Please enter a valid email address.', 'error');
          return;
        }
      }

      loginSendOtpBtn.disabled = true;
      loginSendOtpBtn.innerText = 'Sending...';
      if (loginResendBtn) {
        loginResendBtn.disabled = true;
        loginResendBtn.innerText = 'Sending...';
      }

      try {
        const checkResult = await fetchJsonHelper(`${PROXY_BASE}/check-customer?identifier=${encodeURIComponent(val)}`);

        if (!checkResult.exists) {
          showAlert('No account found. Switching to Create Account page...', 'warning');
          loginSendOtpBtn.disabled = false;
          loginSendOtpBtn.innerText = 'Get OTP';
          if (loginResendBtn) {
            loginResendBtn.disabled = false;
            loginResendBtn.innerText = 'Resend now';
          }
          setTimeout(() => {
            setFormMode('signup');
            if (isEmail) {
              if (signupEmailInput) signupEmailInput.value = val;
            } else {
              if (signupPhoneInput) signupPhoneInput.value = val;
            }
          }, 1500);
          return;
        }

        // Send OTP
        const result = await fetchJsonHelper(`${PROXY_BASE}/send-otp`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ identifier: val })
        });

        loginOtpSent = true;
        if (loginOtpRow) loginOtpRow.style.display = 'block';
        loginIdentifierInput.disabled = true;

        if (submitAuthBtn) {
          submitAuthBtn.style.display = 'flex';
        }

        showAlert('A login code has been sent to your registered credentials.', 'success');
        startCooldown(loginSendOtpBtn, result.nextDelaySeconds || 60, loginResendBtn);

      } catch (err) {
        showAlert(err.message, 'error');
        loginSendOtpBtn.disabled = false;
        loginSendOtpBtn.innerText = 'Get OTP';
        if (loginResendBtn) {
          loginResendBtn.disabled = false;
          loginResendBtn.innerText = 'Resend now';
        }
      }
    });
  }

  // ================= SIGNUP SEND MOBILE OTP =================
  if (signupSendMobileOtpBtn && signupPhoneInput) {
    signupSendMobileOtpBtn.addEventListener('click', async function (e) {
      e.preventDefault();
      hideAlert();

      const countryCode = document.getElementById('mgc-signup-country-code')?.value || '+91';
      let phone = signupPhoneInput.value.trim();
      if (!phone) {
        showAlert('Please enter your mobile number.', 'error');
        return;
      }

      if (/^[0-9]{10}$/.test(phone)) {
        phone = countryCode + phone;
        signupPhoneInput.value = phone;
      } else if (!phone.startsWith('+')) {
        phone = countryCode + phone.replace(/^0+/, '');
        signupPhoneInput.value = phone;
      }

      if (!/^\+?[0-9]{10,15}$/.test(phone)) {
        showAlert('Please enter a valid mobile number.', 'error');
        return;
      }

      signupSendMobileOtpBtn.disabled = true;
      signupSendMobileOtpBtn.innerText = 'Sending...';
      if (signupMobileResendBtn) {
        signupMobileResendBtn.disabled = true;
        signupMobileResendBtn.innerText = 'Sending...';
      }

      try {
        const checkResult = await fetchJsonHelper(`${PROXY_BASE}/check-customer?identifier=${encodeURIComponent(phone)}`);

        if (checkResult.exists) {
          showAlert('Customer already exists. Switching to Login page...', 'error');
          signupSendMobileOtpBtn.disabled = false;
          signupSendMobileOtpBtn.innerText = 'Get OTP';
          if (signupMobileResendBtn) {
            signupMobileResendBtn.disabled = false;
            signupMobileResendBtn.innerText = 'Resend now';
          }
          setTimeout(() => {
            setFormMode('login');
            if (loginIdentifierInput) loginIdentifierInput.value = phone;
          }, 1500);
          return;
        }

        // Send OTP
        const result = await fetchJsonHelper(`${PROXY_BASE}/send-otp`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ identifier: phone })
        });

        signupMobileOtpSent = true;
        updateProgressStep(2);
        if (signupMobileOtpRow) signupMobileOtpRow.style.display = 'block';
        signupPhoneInput.disabled = true;

        if (submitAuthBtn) {
          submitAuthBtn.style.display = 'flex';
        }

        showAlert('Mobile OTP sent successfully. Please check your SMS.', 'success');
        startCooldown(signupSendMobileOtpBtn, result.nextDelaySeconds || 60, signupMobileResendBtn);

      } catch (err) {
        showAlert(err.message, 'error');
        signupSendMobileOtpBtn.disabled = false;
        signupSendMobileOtpBtn.innerText = 'Get OTP';
        if (signupMobileResendBtn) {
          signupMobileResendBtn.disabled = false;
          signupMobileResendBtn.innerText = 'Resend now';
        }
      }
    });
  }

  // ================= SIGNUP SEND EMAIL OTP =================
  if (signupSendEmailOtpBtn && signupEmailInput) {
    signupSendEmailOtpBtn.addEventListener('click', async function (e) {
      e.preventDefault();
      hideAlert();

      const email = signupEmailInput.value.trim();
      if (!email) {
        showAlert('Please enter your email address.', 'error');
        return;
      }

      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
        showAlert('Please enter a valid email address.', 'error');
        return;
      }

      signupSendEmailOtpBtn.disabled = true;
      signupSendEmailOtpBtn.innerText = 'Sending...';
      if (signupEmailResendBtn) {
        signupEmailResendBtn.disabled = true;
        signupEmailResendBtn.innerText = 'Sending...';
      }

      try {
        const checkResult = await fetchJsonHelper(`${PROXY_BASE}/check-customer?identifier=${encodeURIComponent(email)}`);

        if (checkResult.exists) {
          showAlert('Customer already exists with this email address. Switching to Login page...', 'error');
          signupSendEmailOtpBtn.disabled = false;
          signupSendEmailOtpBtn.innerText = 'Get OTP';
          if (signupEmailResendBtn) {
            signupEmailResendBtn.disabled = false;
            signupEmailResendBtn.innerText = 'Resend now';
          }
          setTimeout(() => {
            setFormMode('login');
            if (loginIdentifierInput) loginIdentifierInput.value = email;
          }, 1500);
          return;
        }

        // Send OTP
        const result = await fetchJsonHelper(`${PROXY_BASE}/send-otp`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ identifier: email })
        });

        signupEmailOtpSent = true;
        updateProgressStep(2);
        if (signupEmailOtpRow) signupEmailOtpRow.style.display = 'block';
        signupEmailInput.disabled = true;

        showAlert('Email OTP sent successfully. Please check your Inbox.', 'success');
        startCooldown(signupSendEmailOtpBtn, result.nextDelaySeconds || 60, signupEmailResendBtn);

      } catch (err) {
        showAlert(err.message, 'error');
        signupSendEmailOtpBtn.disabled = false;
        signupSendEmailOtpBtn.innerText = 'Get OTP';
        if (signupEmailResendBtn) {
          signupEmailResendBtn.disabled = false;
          signupEmailResendBtn.innerText = 'Resend now';
        }
      }
    });
  }

  // ================= SIGNUP SEND UNIFIED OTP =================
  if (signupSendUnifiedOtpBtn && signupPhoneInput) {
    signupSendUnifiedOtpBtn.addEventListener('click', async function (e) {
      e.preventDefault();
      hideAlert();

      const countryCode = document.getElementById('mgc-signup-country-code')?.value || '+91';
      let phone = signupPhoneInput.value.trim();
      if (!phone) {
        showAlert('Please enter your mobile number.', 'error');
        return;
      }

      if (/^[0-9]{10}$/.test(phone)) {
        phone = countryCode + phone;
        signupPhoneInput.value = phone;
      } else if (!phone.startsWith('+')) {
        phone = countryCode + phone.replace(/^0+/, '');
        signupPhoneInput.value = phone;
      }

      if (!/^\+?[0-9]{10,15}$/.test(phone)) {
        showAlert('Please enter a valid mobile number.', 'error');
        return;
      }

      const email = signupEmailInput ? signupEmailInput.value.trim() : '';
      if (email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
        showAlert('Please enter a valid email address.', 'error');
        return;
      }

      signupSendUnifiedOtpBtn.disabled = true;
      signupSendUnifiedOtpBtn.innerText = 'Sending...';

      try {
        const checkResultPhone = await fetchJsonHelper(`${PROXY_BASE}/check-customer?identifier=${encodeURIComponent(phone)}`);
        if (checkResultPhone.exists) {
          showAlert('Customer already exists with this mobile number. Switching to Login page...', 'error');
          signupSendUnifiedOtpBtn.disabled = false;
          signupSendUnifiedOtpBtn.innerText = 'Get OTP';
          setTimeout(() => {
            setFormMode('login');
            if (loginIdentifierInput) loginIdentifierInput.value = phone;
          }, 1500);
          return;
        }

        if (email) {
          const checkResultEmail = await fetchJsonHelper(`${PROXY_BASE}/check-customer?identifier=${encodeURIComponent(email)}`);
          if (checkResultEmail.exists) {
            showAlert('Customer already exists with this email address. Switching to Login page...', 'error');
            signupSendUnifiedOtpBtn.disabled = false;
            signupSendUnifiedOtpBtn.innerText = 'Get OTP';
            setTimeout(() => {
              setFormMode('login');
              if (loginIdentifierInput) loginIdentifierInput.value = email;
            }, 1500);
            return;
          }
        }

        const sendPayload = { identifier: phone };
        if (email) {
          sendPayload.email = email;
        }

        const result = await fetchJsonHelper(`${PROXY_BASE}/send-otp`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(sendPayload)
        });

        signupMobileOtpSent = true;
        signupEmailOtpSent = true;
        updateProgressStep(2);

        if (signupMobileOtpRow) signupMobileOtpRow.style.display = 'block';
        signupPhoneInput.disabled = true;
        if (signupEmailInput) signupEmailInput.disabled = true;

        if (submitAuthBtn) {
          submitAuthBtn.style.display = 'flex';
        }

        const successMsg = email
          ? 'OTP code sent successfully to your Mobile and Email.'
          : 'OTP code sent successfully to your Mobile.';
        showAlert(successMsg, 'success');
        startCooldown(signupSendUnifiedOtpBtn, result.nextDelaySeconds || 60, signupMobileResendBtn);

      } catch (err) {
        showAlert(err.message, 'error');
        signupSendUnifiedOtpBtn.disabled = false;
        signupSendUnifiedOtpBtn.innerText = 'Get OTP';
      }
    });
  }

  // ================= SUBMIT VERIFICATION =================
  authForm.addEventListener('submit', async function (e) {
    e.preventDefault();
    hideAlert();

    let reqBody = {};

    if (formMode !== 'signup') {
      // Login Mode Submission
      const val = loginIdentifierInput.value.trim();
      const otp = loginOtpInput ? loginOtpInput.value.trim() : '';

      if (!loginOtpSent) {
        showAlert('Please request an OTP first.', 'error');
        return;
      }
      if (otp.length !== 6) {
        showAlert('Please enter the full 6-digit OTP.', 'error');
        return;
      }

      reqBody = { identifier: val, otp: otp };
    } else {
      // Signup Mode Submission
      const phone = signupPhoneInput.value.trim();
      const mobileOtp = signupMobileOtpInput ? signupMobileOtpInput.value.trim() : '';
      const email = signupEmailInput ? signupEmailInput.value.trim() : '';
      const emailOtp = signupEmailOtpInput ? signupEmailOtpInput.value.trim() : '';
      const name = signupNameInput ? signupNameInput.value.trim() : '';
      const city = signupCityInput ? signupCityInput.value.trim() : '';
      let referralCode = signupReferralInput ? signupReferralInput.value.trim() : '';

      if (!phone || !signupMobileOtpSent) {
        showAlert('Please verify your mobile number. It is compulsory for signup.', 'error');
        return;
      }
      if (mobileOtp.length !== 6) {
        showAlert('Please enter the 6-digit Mobile OTP.', 'error');
        return;
      }

      if (!name) {
        showAlert('Full Name is required.', 'error');
        return;
      }
      if (!city) {
        showAlert('City is required.', 'error');
        return;
      }

      // If email is entered, verify it
      if (email && otpCodeMode !== 'same') {
        if (!signupEmailOtpSent) {
          showAlert('You have entered an email. Please click "Get OTP" next to the email and verify it.', 'error');
          return;
        }
        if (emailOtp.length !== 6) {
          showAlert('Please enter the 6-digit Email OTP.', 'error');
          return;
        }
      }

      // Check legal consent checkbox
      if (signupLegalConsentCheckbox && !signupLegalConsentCheckbox.checked) {
        showAlert('Please accept the Terms & Conditions to continue.', 'error');
        return;
      }

      if (referralCode) {
        referralCode = referralCode.trim().toUpperCase().replace(/[^A-Z0-9]/g, '');
      }

      reqBody = {
        identifier: phone, // phone is the primary identifier for signup
        otp: mobileOtp,
        name,
        city,
        referralCode,
        phone,
        isSignup: true,
        acceptsEmailMarketing: email && emailConsentInput ? emailConsentInput.checked : false,
        acceptsSmsMarketing: smsConsentInput ? smsConsentInput.checked : false,
        termsAccepted: signupLegalConsentCheckbox ? signupLegalConsentCheckbox.checked : false
      };

      if (email) {
        reqBody.email = email;
        if (otpCodeMode !== 'same') {
          reqBody.emailOtp = emailOtp;
        }
      }
    }

    submitAuthBtn.disabled = true;
    const originalBtnText = submitAuthBtnText ? submitAuthBtnText.textContent : submitAuthBtn.innerText;
    if (submitAuthBtnText) submitAuthBtnText.textContent = 'Verifying...';

    try {
      const result = await fetchJsonHelper(`${PROXY_BASE}/verify-otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(reqBody)
      });

      // Save JWT token locally
      const sessionToken = result.mgcSessionToken || result.token;
      localStorage.setItem('mgc_session_token', sessionToken);

      updateProgressStep(3);

      let targetUrl = result.redirectUrl || '/pages/mgc-dashboard';
      try {
        const currentUrl = new URL(window.location.href);
        const returnUrl = currentUrl.searchParams.get('checkout_url') || currentUrl.searchParams.get('return_to') || currentUrl.searchParams.get('redirect');
        if (returnUrl) {
          targetUrl = returnUrl;
        }
      } catch (e) { }

      const isCheckoutRedirect = targetUrl === '/checkout' || targetUrl.includes('/checkout');

      // Handle referral warning logic without blocking redirection
      let redirectDelay = isCheckoutRedirect ? 5000 : 1500;
      if (formMode === 'signup' && reqBody.referralCode && !result.referralApplied) {
        if (isCheckoutRedirect) {
          showAlert('Your account was verified. Redirecting to checkout in 5 seconds...', 'warning');
        } else {
          showAlert('Your account was verified, but the referral code could not be applied.', 'warning');
          redirectDelay = 3000;
        }
      } else {
        if (isCheckoutRedirect) {
          showAlert('Verification successful! Redirecting to checkout in 5 seconds...', 'success');
        } else {
          showAlert('Verification successful. Logging in...', 'success');
        }
      }

      if (isCheckoutRedirect) {
        let remainingSec = 5;
        const countdownInterval = setInterval(() => {
          remainingSec--;
          if (remainingSec > 0) {
            const descEl = document.getElementById('mgc-alert-desc');
            if (descEl) {
              descEl.textContent = `Verification successful! Redirecting to checkout in ${remainingSec} seconds...`;
            }
          } else {
            clearInterval(countdownInterval);
          }
        }, 1000);
      }

      mgcLog('OTP verified. Redirecting to:', targetUrl);
      setTimeout(async () => {
        let finalUrl = targetUrl;
        try {
          const currentUrl = new URL(window.location.href);
          const targetUrlObj = new URL(targetUrl, window.location.origin);

          if (targetUrlObj.pathname === '/checkout' || targetUrlObj.pathname.includes('/checkout')) {
            mgcLog("Redirect target is checkout. Fetching pre-fill params...");
            const checkoutParams = await getCustomerCheckoutParams();
            if (checkoutParams) {
              mgcLog("Pre-fill params obtained:", checkoutParams);
              const newParams = new URLSearchParams(checkoutParams);
              for (const [key, val] of newParams.entries()) {
                targetUrlObj.searchParams.set(key, val);
              }
            } else {
              mgcLog("No pre-fill params obtained.");
            }
          }

          const paramsToPreserve = ['preview_theme_id', '_preview_theme_id', '_fd', '_iframe'];
          paramsToPreserve.forEach(param => {
            if (currentUrl.searchParams.has(param)) {
              targetUrlObj.searchParams.set(param, currentUrl.searchParams.get(param));
            }
          });
          finalUrl = targetUrlObj.pathname + targetUrlObj.search;
        } catch (e) {
          console.error('Failed to preserve preview parameters / pre-fill checkout:', e);
        }
        window.location.href = finalUrl;
      }, redirectDelay);

    } catch (err) {
      showAlert(err.message, 'error');
      submitAuthBtn.disabled = false;
      if (submitAuthBtnText) {
        submitAuthBtnText.textContent = originalBtnText;
      } else {
        submitAuthBtn.innerText = originalBtnText;
      }
    }
  });

  // If referral code is detected in URL, auto-switch to signup mode
  const urlParams = new URLSearchParams(window.location.search);
  if (urlParams.has('ref')) {
    mgcLog("Referral code found in URL. Switching to signup mode.");
    setFormMode('signup');
  }
}

/**
 * Handle Customer dashboard stats fetching and actions
 */
async function initializeDashboard() {
  const container = document.getElementById('mgc-dashboard-view');
  if (!container) return;

  const token = localStorage.getItem('mgc_session_token');

  // If the token is missing, immediately show the unauth view without calling backend
  if (!token) {
    mgcWarn('No session token found in localStorage.');
    document.getElementById('mgc-dashboard-skeleton').style.display = 'none';
    document.getElementById('mgc-dashboard-content').style.display = 'none';
    document.getElementById('mgc-dashboard-unauth-msg').style.display = 'block';
    return;
  }

  const headers = {
    'Authorization': `Bearer ${token}`
  };

  try {
    // Parallel fetching for faster loading speed
    const dashboardPromise = fetchJsonHelper(`${PROXY_BASE}/dashboard`, {
      method: 'GET',
      headers: headers
    });

    const couponsPromise = fetchJsonHelper(`${PROXY_BASE}/my-coupons`, {
      method: 'GET',
      headers: headers
    }).catch(err => {
      console.error('Failed to load coupons:', err);
      return [];
    });

    const transactionsPromise = fetchJsonHelper(`${PROXY_BASE}/my-transactions`, {
      method: 'GET',
      headers: headers
    }).catch(err => {
      console.error('Failed to load transactions:', err);
      return [];
    });

    const milestonesPromise = fetchJsonHelper(`${PROXY_BASE}/purchase-tier-rewards`, {
      method: 'GET',
      headers: headers
    }).catch(err => {
      console.error('Failed to load purchase milestones:', err);
      return null;
    });

    const [result, coupons, transactions, milestones] = await Promise.all([
      dashboardPromise,
      couponsPromise,
      transactionsPromise,
      milestonesPromise
    ]);

    const customerData = result.customer || result;
    window.mgcCustomerData = customerData;
    const displayName = customerData.name || 'Customer';
    const displayCity = customerData.city || 'N/A';
    const displayCoins = customerData.mgcCoins !== undefined ? customerData.mgcCoins : (customerData.coins || 0);
    const displayCode = customerData.referralCode || '';
    const displayLink = customerData.referralLink || '';
    const displayTotalReferrals = customerData.totalReferrals !== undefined ? customerData.totalReferrals : 0;
    const displayIdentifier = customerData.email || customerData.phone || 'N/A';
    const displayReferredCustomers = customerData.referredCustomers || [];

    // Cache details in localStorage for checkout auto-fill
    localStorage.setItem('mgc_customer_email', customerData.email || '');
    localStorage.setItem('mgc_customer_phone', customerData.phone || '');
    localStorage.setItem('mgc_customer_name', displayName);
    localStorage.setItem('mgc_customer_city', displayCity);

    // Tiers and Statistics
    const displayTier = customerData.customerTier || 'Regular Referrer';
    const totalEarned = customerData.totalEarnedCoins !== undefined ? customerData.totalEarnedCoins : displayCoins;
    const totalRedeemed = customerData.totalRedeemedCoins !== undefined ? customerData.totalRedeemedCoins : 0;

    // Populate dashboard data
    const userNameEl = document.getElementById('mgc-user-name');
    if (userNameEl) userNameEl.textContent = displayName;

    const userCityEl = document.getElementById('mgc-user-city');
    if (userCityEl) userCityEl.textContent = displayCity;

    const coinCountEl = document.getElementById('mgc-coin-count');
    if (coinCountEl) coinCountEl.textContent = displayCoins;

    // Update rupee value (1 MGC = ₹0.25)
    const rupeeValEl = document.getElementById('mgc-rupee-value');
    if (rupeeValEl) {
      const rupeeAmt = (displayCoins * 0.25).toFixed(2);
      rupeeValEl.textContent = `₹${rupeeAmt}`;
    }

    // Set new metrics
    const userTierEl = document.getElementById('mgc-user-tier');
    if (userTierEl) userTierEl.textContent = displayTier;

    const earnedEl = document.getElementById('mgc-total-earned');
    if (earnedEl) earnedEl.textContent = `${totalEarned} MGC`;

    const redeemedEl = document.getElementById('mgc-total-redeemed');
    if (redeemedEl) redeemedEl.textContent = `${totalRedeemed} MGC`;

    // Render Progress Card
    const progressCard = document.getElementById('mgc-tier-progress-card');
    if (progressCard && customerData.progress) {
      const prog = customerData.progress;
      if (prog.enabled) {
        progressCard.style.display = 'block';

        const nextTierNameEl = document.getElementById('mgc-next-tier-name');
        if (nextTierNameEl) nextTierNameEl.textContent = prog.nextTierName;

        const progressBar = document.getElementById('mgc-tier-progress-bar');
        if (progressBar) progressBar.style.width = `${prog.percentage}%`;

        const progressCurrent = document.getElementById('mgc-tier-progress-current');
        if (progressCurrent) progressCurrent.textContent = prog.current;

        const progressPct = document.getElementById('mgc-tier-progress-pct');
        if (progressPct) progressPct.textContent = `${prog.percentage}%`;

        const progressDesc = document.getElementById('mgc-tier-progress-desc');
        if (progressDesc) {
          if (prog.coinsNeeded > 0) {
            progressDesc.textContent = `Refer ${prog.coinsNeeded} more friend(s) to unlock the ${prog.nextTierName} tier!`;
          } else {
            progressDesc.textContent = `You've achieved the highest tier milestone! Excellent work!`;
          }
        }
      } else {
        progressCard.style.display = 'none';
      }
    }

    const identifierEl = document.getElementById('mgc-user-identifier');
    if (identifierEl) {
      identifierEl.textContent = displayIdentifier;
    }

    const codeInput = document.getElementById('mgc-dash-code');
    const linkInput = document.getElementById('mgc-dash-link');

    if (codeInput) {
      if (codeInput.tagName === 'INPUT' || codeInput.tagName === 'TEXTAREA') {
        codeInput.value = displayCode;
      } else {
        codeInput.textContent = displayCode;
      }
    }
    if (linkInput) {
      if (linkInput.tagName === 'INPUT' || linkInput.tagName === 'TEXTAREA') {
        linkInput.value = displayLink;
      } else {
        linkInput.textContent = displayLink;
      }
    }

    const totalReferralsEl = document.getElementById('mgc-total-referrals');
    if (totalReferralsEl) {
      totalReferralsEl.textContent = displayTotalReferrals;
    }
    const bonusValEl = document.querySelector('.mgc-bonus-val');
    if (bonusValEl) {
      bonusValEl.textContent = `${displayTotalReferrals * 100} MGC`;
    }

    // Render Referred Friends List
    const referredEmpty = document.getElementById('mgc-referred-empty');
    const referredWrapper = document.getElementById('mgc-referred-list-wrapper');
    const referredList = document.getElementById('mgc-referred-friends-list');

    if (referredList) {
      referredList.innerHTML = '';

      if (displayReferredCustomers.length === 0) {
        if (displayTotalReferrals > 0) {
          if (referredEmpty) referredEmpty.style.display = 'none';
          if (referredWrapper) referredWrapper.style.display = 'block';
          for (let i = 0; i < displayTotalReferrals; i++) {
            const li = document.createElement('li');
            li.className = 'mgc-referred-item';

            const identitySpan = document.createElement('span');
            identitySpan.innerHTML = `<strong>Referred Friend</strong>`;

            const badgeSpan = document.createElement('span');
            badgeSpan.className = `mgc-badge mgc-badge-rewarded`;
            badgeSpan.textContent = 'Rewarded';

            li.appendChild(identitySpan);
            li.appendChild(badgeSpan);
            referredList.appendChild(li);
          }
        } else {
          if (referredEmpty) referredEmpty.style.display = 'block';
          if (referredWrapper) referredWrapper.style.display = 'none';
        }
      } else {
        if (referredEmpty) referredEmpty.style.display = 'none';
        if (referredWrapper) referredWrapper.style.display = 'block';

        displayReferredCustomers.forEach(friend => {
          const li = document.createElement('li');
          li.className = 'mgc-referred-item';

          // Format email/phone with masking
          let rawIdentity = friend.email || friend.phone || 'Unknown Friend';
          let identityText = rawIdentity;
          if (rawIdentity.includes('@')) {
            const parts = rawIdentity.split('@');
            const first = parts[0];
            const mask = first.length > 2 ? first.substring(0, 2) + '***' : first + '***';
            identityText = `${mask}@${parts[1]}`;
          } else if (/^[0-9+]+$/.test(rawIdentity)) {
            identityText = rawIdentity.substring(0, 4) + '***' + rawIdentity.substring(rawIdentity.length - 3);
          }

          const identitySpan = document.createElement('span');
          identitySpan.innerHTML = `<strong>${identityText}</strong>`;

          const badgeSpan = document.createElement('span');
          const cleanStatus = (friend.status || 'pending').toLowerCase();
          badgeSpan.className = `mgc-badge mgc-badge-${cleanStatus}`;
          badgeSpan.textContent = cleanStatus === 'rewarded' ? 'Rewarded' : 'Pending';

          li.appendChild(identitySpan);
          li.appendChild(badgeSpan);
          referredList.appendChild(li);
        });
      }
    }

    // Toggle dashboard manually apply referral box if they haven't been referred yet
    const manualReferralBox = document.getElementById('mgc-manual-referral');
    if (manualReferralBox) {
      const referredBy = customerData.referredBy || result.referredBy || customerData.referred_by;
      if (referredBy) {
        manualReferralBox.style.display = 'none';
      } else {
        manualReferralBox.style.display = 'block';
      }
    }

    // Render coupons
    try {
      if (coupons) {
        renderCouponsList(coupons, displayCoins);
        setupCouponRedeemHandlers(headers);
      }
    } catch (couponErr) {
      console.error('Failed to render coupons:', couponErr);
    }

    // Render transactions
    try {
      if (transactions) {
        renderTransactionsList(transactions);
      }
    } catch (txErr) {
      console.error('Failed to render transactions:', txErr);
    }

    // Render orders
    try {
      renderOrdersList(customerData.orders || []);
    } catch (ordersErr) {
      console.error('Failed to render orders:', ordersErr);
    }

    // Render purchase milestones
    try {
      if (milestones) {
        await fetchAndRenderPurchaseMilestones(headers, milestones);
      }
    } catch (milestoneErr) {
      console.error('Failed to render purchase milestones:', milestoneErr);
    }

    // Switch views
    document.getElementById('mgc-dashboard-skeleton').style.display = 'none';
    document.getElementById('mgc-dashboard-unauth-msg').style.display = 'none';
    document.getElementById('mgc-dashboard-content').style.display = 'block';

    // Hook buttons
    setupDashboardActions(displayCode, displayLink);

    // Set up logout button listener
    const logoutBtn = document.getElementById('mgc-logout-btn');
    if (logoutBtn) {
      logoutBtn.addEventListener('click', function () {
        localStorage.removeItem('mgc_session_token');
        localStorage.removeItem('mgc_customer_email');
        localStorage.removeItem('mgc_customer_phone');
        localStorage.removeItem('mgc_customer_name');
        localStorage.removeItem('mgc_customer_city');
        mgcLog('Session token cleared. Redirecting to OTP login page.');
        let finalUrl = '/pages/mgc-login';
        try {
          const currentUrl = new URL(window.location.href);
          const targetUrlObj = new URL(finalUrl, window.location.origin);
          const paramsToPreserve = ['preview_theme_id', '_preview_theme_id', '_fd', '_iframe'];
          paramsToPreserve.forEach(param => {
            if (currentUrl.searchParams.has(param)) {
              targetUrlObj.searchParams.set(param, currentUrl.searchParams.get(param));
            }
          });
          finalUrl = targetUrlObj.pathname + targetUrlObj.search;
        } catch (e) {
          console.error('Failed to preserve preview parameters:', e);
        }
        window.location.href = finalUrl;
      });
    }

  } catch (err) {
    mgcLog('Error rendering dashboard:', err);
    const errMsg = err ? (err.message || String(err)) : '';
    if (errMsg.includes('401') || errMsg.includes('Unauthorized') || errMsg.includes('routing error')) {
      localStorage.removeItem('mgc_session_token');
      document.getElementById('mgc-dashboard-skeleton').style.display = 'none';
      document.getElementById('mgc-dashboard-content').style.display = 'none';
      document.getElementById('mgc-dashboard-unauth-msg').style.display = 'block';
    } else {
      document.getElementById('mgc-dashboard-skeleton').style.display = 'none';
      document.getElementById('mgc-dashboard-content').style.display = 'none';
      document.getElementById('mgc-dashboard-error').style.display = 'block';
    }
  }
}

function setupDashboardActions(code, link) {
  // Copy Referral Code
  const copyCodeBtn = document.getElementById('mgc-copy-code-btn');
  if (copyCodeBtn) {
    copyCodeBtn.addEventListener('click', function () {
      navigator.clipboard.writeText(code).then(() => {
        const originalText = this.innerText;
        this.innerText = 'check';
        this.classList.add('text-tertiary');
        setTimeout(() => {
          this.innerText = originalText;
          this.classList.remove('text-tertiary');
        }, 2000);
      });
    });
  }

  // Copy Referral Link
  const copyLinkBtn = document.getElementById('mgc-copy-link-btn');
  if (copyLinkBtn) {
    copyLinkBtn.addEventListener('click', function () {
      navigator.clipboard.writeText(link).then(() => {
        const originalText = this.innerText;
        this.innerText = 'Link Copied!';
        setTimeout(() => this.innerText = originalText, 2000);
      });
    });
  }

  // WhatsApp Share
  const whatsappBtn = document.getElementById('mgc-whatsapp-share-btn');
  if (whatsappBtn) {
    whatsappBtn.addEventListener('click', function () {
      const message = `Hey! Check out MGCKart. Register with my referral code ${code} or click this link to instantly earn 100 MGC coins: ${link}`;
      const url = `https://api.whatsapp.com/send?text=${encodeURIComponent(message)}`;
      window.open(url, '_blank');
    });
  }

  // Manual Referral Code Apply
  const manualForm = document.getElementById('mgc-manual-referral-form');
  if (manualForm) {
    manualForm.addEventListener('submit', async function (e) {
      e.preventDefault();
      const codeToApply = document.getElementById('mgc-manual-code').value.trim();
      const applyBtn = manualForm.querySelector('button[type="submit"]');
      const alertDiv = document.getElementById('mgc-manual-alert');

      if (!codeToApply) return;

      applyBtn.disabled = true;
      applyBtn.innerText = 'Applying...';
      alertDiv.style.display = 'none';

      const token = localStorage.getItem('mgc_session_token');
      const headers = { 'Content-Type': 'application/json' };
      if (token) headers['Authorization'] = `Bearer ${token}`;

      try {
        const data = await fetchJsonHelper(`${PROXY_BASE}/apply-referral`, {
          method: 'POST',
          headers: headers,
          body: JSON.stringify({ referralCode: codeToApply })
        });

        alertDiv.textContent = 'Referral applied successfully! +100 MGC Coins!';
        alertDiv.className = 'mgc-alert mgc-alert-success';
        alertDiv.style.display = 'block';

        // Update coins in dashboard
        document.getElementById('mgc-coin-count').textContent = data.coins;

        // Hide form after delay
        setTimeout(() => {
          document.getElementById('mgc-manual-referral').style.display = 'none';
        }, 3000);

      } catch (err) {
        alertDiv.textContent = err.message;
        alertDiv.className = 'mgc-alert mgc-alert-error';
        alertDiv.style.display = 'block';
      } finally {
        applyBtn.disabled = false;
        applyBtn.innerText = 'Apply Code';
      }
    });
  }

  // Orders Toggle Button
  const ordersToggleBtn = document.getElementById('mgc-orders-toggle-btn');
  if (ordersToggleBtn) {
    ordersToggleBtn.addEventListener('click', function () {
      isOrdersCollapsed = !isOrdersCollapsed;
      renderOrdersList(currentOrders);
    });
  }
}

/**
 * Cookie Helper
 */
function getCookie(name) {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) return decodeURIComponent(parts.pop().split(';').shift());
  return '';
}

/**
 * Global Referral Logic (Issue 8)
 */
function initializeGlobalReferral() {
  const REF_PARAM_NAME = 'ref';
  const STORAGE_KEY = 'mgckart_referral_code';
  const COOKIE_KEY = 'mgckart_referral_code';
  const REF_EXPIRY_DAYS = 30;

  // Get parameter by name from URL
  function getParameterByName(name) {
    const url = window.location.href;
    const cleanName = name.replace(/[\[\]]/g, '\\$&');
    const regex = new RegExp('[?&]' + cleanName + '(=([^&#]*)|&|#|$)');
    const results = regex.exec(url);
    if (!results) return null;
    if (!results[2]) return '';
    return decodeURIComponent(results[2].replace(/\+/g, ' '));
  }

  // Set cookie helper
  function setCookie(name, value, days) {
    const date = new Date();
    date.setTime(date.getTime() + (days * 24 * 60 * 60 * 1000));
    const expires = "; expires=" + date.toUTCString();
    document.cookie = name + "=" + encodeURIComponent(value) + expires + "; path=/; SameSite=Lax";
  }

  // Normalize referral code
  function normalizeReferralCode(code) {
    if (!code) return '';
    // trim, uppercase, allow only A-Z, 0-9, hyphen, underscore, max length 50
    return code
      .trim()
      .toUpperCase()
      .replace(/[^A-Z0-9\-_]/g, '')
      .substring(0, 50);
  }

  // Sync function to Shopify cart
  async function syncReferralToCart(referralCode) {
    if (!referralCode) return;
    mgcLog("Syncing referral code to cart:", referralCode);
    try {
      const response = await fetch('/cart/update.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          attributes: {
            referral_code: referralCode,
            mgc_referral_code: referralCode,
            _mgc_referral_code: referralCode
          }
        })
      });
      const data = await response.json();
      mgcLog("Referral synced to cart successfully", data.attributes);
    } catch (err) {
      console.error("[MGC] Failed to sync referral to cart:", err);
    }
  }

  // Update hidden inputs in any cart form
  function updateHiddenInputs(referralCode) {
    if (!referralCode) return;

    // By ID
    const idFields = [
      'MgcCartReferralCode',
      'MgcCartReferralCodeAlt',
      'MgcMiniCartReferralCode',
      'MgcMiniCartReferralCodeAlt'
    ];
    idFields.forEach(id => {
      const el = document.getElementById(id);
      if (el) el.value = referralCode;
    });

    // By Name
    const nameFields = [
      'attributes[referral_code]',
      'attributes[mgc_referral_code]',
      'attributes[_mgc_referral_code]'
    ];
    nameFields.forEach(name => {
      const elements = document.querySelectorAll(`input[name="${name}"]`);
      elements.forEach(el => el.value = referralCode);
    });
  }

  // Step 1: Capture or Restore referral code
  let referralCode = getParameterByName(REF_PARAM_NAME);

  if (referralCode) {
    referralCode = normalizeReferralCode(referralCode);
    if (referralCode) {
      mgcLog("Captured referral code from URL:", referralCode);
      localStorage.setItem(STORAGE_KEY, referralCode);
      setCookie(COOKIE_KEY, referralCode, REF_EXPIRY_DAYS);
      syncReferralToCart(referralCode);
    }
  } else {
    // Restore
    let storedCode = localStorage.getItem(STORAGE_KEY);
    if (!storedCode) {
      storedCode = getCookie(COOKIE_KEY);
    }
    if (storedCode) {
      referralCode = normalizeReferralCode(storedCode);
      mgcLog("Restored referral code from storage:", referralCode);
      syncReferralToCart(referralCode);
    }
  }

  // Update fields if we have a referral code
  if (referralCode) {
    updateHiddenInputs(referralCode);
  }

  // Watch for dynamic cart loads (e.g. mini-cart open or cart drawer render)
  const observer = new MutationObserver(() => {
    if (referralCode) {
      updateHiddenInputs(referralCode);
    }
  });
  observer.observe(document.body, { childList: true, subtree: true });

  // Step 2: Checkout Behavior Interception (Includes Cart Checkout & Direct Buy Now buttons)
  document.addEventListener('click', async function (event) {
    // Add click handler for buttons or links going to checkout or direct Buy Now
    const checkoutSelector = 'form[action="/cart"] [name="checkout"], a[href="/checkout"], a[href*="/checkout"], .btn-checkout, button[name="checkout"], input[name="checkout"], .shopify-payment-button__button, .shopify-payment-button button, [data-testid="Checkout-button"], .product-group-buy-now, .bls-btn-checkout-qickview, .bls__product-dynamic-checkout button';
    const target = event.target.closest(checkoutSelector);
    if (!target) return;

    // Prevent default checkout navigation
    event.preventDefault();
    event.stopPropagation();

    // Check if it's a Buy Now / Product Form button
    const isBuyNowButton = target.classList.contains('shopify-payment-button__button') || 
                           target.closest('.shopify-payment-button') || 
                           target.closest('.bls__product-dynamic-checkout') ||
                           target.classList.contains('product-group-buy-now') ||
                           target.classList.contains('bls-btn-checkout-qickview');

    if (isBuyNowButton) {
      const productForm = target.closest('form');
      if (productForm) {
        try {
          const formData = new FormData(productForm);
          formData.delete('sections');
          formData.delete('sections_url');
          await fetch('/cart/add.js', {
            method: 'POST',
            body: formData
          });
        } catch (e) {
          console.error('[MGC] Failed to add item to cart on Buy Now click:', e);
        }
      }
    }

    // 1. Enforce Login requirement before Checkout
    const token = localStorage.getItem('mgc_session_token');
    if (!token) {
      mgcLog("No active session found. Redirecting to OTP login page before checkout.");
      window.location.href = '/pages/mgc-login?checkout_url=/checkout';
      return;
    }

    // 2. Sync referral code if stored
    let currentRef = localStorage.getItem(STORAGE_KEY) || getCookie(COOKIE_KEY);
    if (currentRef) {
      currentRef = normalizeReferralCode(currentRef);
      if (currentRef) {
        mgcLog("Intercepted checkout. Syncing referral code before redirect...", currentRef);
        await syncReferralToCart(currentRef);
      }
    }

    // 3. Obtain customer parameters to auto-fill checkout contact/delivery fields
    const checkoutParams = await getCustomerCheckoutParams();
    const redirectUrl = checkoutParams ? `/checkout?${checkoutParams}` : '/checkout';

    mgcLog("Redirecting to checkout with parameters:", redirectUrl);
    window.location.href = redirectUrl;
  }, true); // Use capture phase to intercept before other theme scripts
}

function renderCouponsList(coupons, currentCoins) {
  // Update redeem buttons disabled state
  const btn100 = document.getElementById('mgc-redeem-100-btn');
  const btn250 = document.getElementById('mgc-redeem-250-btn');
  const btn500 = document.getElementById('mgc-redeem-500-btn');

  if (btn100) {
    if (currentCoins >= 400) {
      btn100.disabled = false;
      btn100.style.opacity = '1';
      btn100.style.cursor = 'pointer';
      btn100.classList.remove('opacity-50', 'cursor-not-allowed');
    } else {
      btn100.disabled = true;
      btn100.style.opacity = '0.5';
      btn100.style.cursor = 'not-allowed';
      btn100.classList.add('opacity-50', 'cursor-not-allowed');
    }
  }

  if (btn250) {
    if (currentCoins >= 1000) {
      btn250.disabled = false;
      btn250.style.opacity = '1';
      btn250.style.cursor = 'pointer';
      btn250.classList.remove('opacity-50', 'cursor-not-allowed');
    } else {
      btn250.disabled = true;
      btn250.style.opacity = '0.5';
      btn250.style.cursor = 'not-allowed';
      btn250.classList.add('opacity-50', 'cursor-not-allowed');
    }
  }

  if (btn500) {
    if (currentCoins >= 2000) {
      btn500.disabled = false;
      btn500.style.opacity = '1';
      btn500.style.cursor = 'pointer';
      btn500.classList.remove('opacity-50', 'cursor-not-allowed');
    } else {
      btn500.disabled = true;
      btn500.style.opacity = '0.5';
      btn500.style.cursor = 'not-allowed';
      btn500.classList.add('opacity-50', 'cursor-not-allowed');
    }
  }

  // Render bento card grid if container exists
  const couponsGrid = document.getElementById('mgc-coupons-grid');
  if (couponsGrid) {
    couponsGrid.innerHTML = '';
    if (!coupons || coupons.length === 0) {
      couponsGrid.innerHTML = `
        <div class="col-span-full text-center py-12 text-on-surface-variant/70 font-semibold bg-white border rounded-2xl">
          <span class="material-symbols-outlined text-4xl block mb-2 opacity-55">confirmation_number</span>
          No coupons generated yet.
        </div>
      `;
    } else {
      coupons.forEach(c => {
        const isUsed = c.status === 'used';
        const isExpired = c.status === 'expired';
        const isCancelled = c.status === 'cancelled' || c.status === 'invalid';
        const isRefunded = c.status === 'refunded';
        const isValid = !isUsed && !isExpired && !isCancelled && !isRefunded;

        let badgeColorClass = 'bg-primary/10 text-primary border border-primary/20';
        let badgeText = 'VALID';
        if (isUsed) {
          badgeColorClass = 'bg-surface-container text-outline';
          badgeText = 'USED';
        } else if (isExpired) {
          badgeColorClass = 'bg-error/10 text-error border border-error/20';
          badgeText = 'EXPIRED';
        } else if (isCancelled) {
          badgeColorClass = 'bg-error/10 text-error border border-error/20';
          badgeText = 'CANCELLED';
        } else if (isRefunded) {
          badgeColorClass = 'bg-surface-container text-outline';
          badgeText = 'REFUNDED';
        }

        const card = document.createElement('div');
        card.className = 'bento-card p-6 bg-gradient-to-br from-white to-surface-container border-dashed border-2 relative overflow-hidden flex flex-col justify-between min-h-[220px]';
        card.innerHTML = `
          <div class="absolute -right-4 -top-4 w-16 h-16 bg-primary-container/20 rounded-full"></div>
          <div>
            <div class="mb-4">
              <span class="font-label-md text-label-md ${badgeColorClass} px-2 py-1 rounded">${badgeText}</span>
            </div>
            <h4 class="font-headline-lg text-headline-lg text-on-surface">₹${c.coupon_value} OFF</h4>
            <p class="font-body-sm text-body-sm text-on-surface-variant mt-2">Flat discount on any order above ₹${c.min_order_value}</p>
          </div>
          <div class="mt-6 flex items-center justify-between border-t border-outline-variant/30 pt-4">
            <span class="font-mono text-title-md font-bold text-on-surface-variant select-all">${c.coupon_code}</span>
            ${isValid ? `
              <button class="text-primary font-label-md text-label-md hover:underline font-bold" onclick="navigator.clipboard.writeText('${c.coupon_code}'); alert('Coupon code copied!');">COPY</button>
            ` : ''}
          </div>
        `;
        couponsGrid.appendChild(card);
      });
    }
  }

  // Fallback: Populate table
  const tbody = document.getElementById('mgc-coupons-table-body');
  if (tbody) {
    tbody.innerHTML = '';
    if (!coupons || coupons.length === 0) {
      tbody.innerHTML = `
        <tr>
          <td colspan="6" style="padding: 20px 8px; text-align: center; color: #6d797d;">No coupons generated yet.</td>
        </tr>
      `;
      return;
    }

    coupons.forEach(c => {
      const tr = document.createElement('tr');
      tr.style.borderBottom = '1px solid #E5E7EB';

      const codeTd = document.createElement('td');
      codeTd.style.padding = '12px 8px';
      codeTd.style.fontFamily = 'monospace';
      codeTd.style.fontWeight = 'bold';
      codeTd.style.fontSize = '14px';
      codeTd.style.display = 'flex';
      codeTd.style.alignItems = 'center';
      codeTd.style.gap = '8px';

      codeTd.innerHTML = `
        <span>${c.coupon_code}</span>
        ${c.status === 'active' ? `
          <button class="mgc-btn-copy-mini" onclick="navigator.clipboard.writeText('${c.coupon_code}'); alert('Coupon code copied!');" style="background: none; border: none; cursor: pointer; display: inline-flex; align-items: center; padding: 2px; color: var(--brand-color-dark);">
            <span class="material-symbols-outlined" style="font-size: 16px;">content_copy</span>
          </button>
        ` : ''}
      `;

      const valTd = document.createElement('td');
      valTd.style.padding = '12px 8px';
      valTd.textContent = `₹${c.coupon_value}`;

      const minTd = document.createElement('td');
      minTd.style.padding = '12px 8px';
      minTd.textContent = `₹${c.min_order_value}`;

      const statusTd = document.createElement('td');
      statusTd.style.padding = '12px 8px';
      const status = (c.status || 'active').toLowerCase();
      let statusColor = '#059669'; // Green for active
      let statusText = 'Active';
      if (status === 'used') {
        statusColor = '#6b7280'; // Gray
        statusText = 'Used';
      } else if (status === 'expired') {
        statusColor = '#dc2626'; // Red
        statusText = 'Expired';
      }
      statusTd.innerHTML = `<span style="color: ${statusColor}; font-weight: 600; text-transform: capitalize;">${statusText}</span>`;

      const createdTd = document.createElement('td');
      createdTd.style.padding = '12px 8px';
      createdTd.textContent = c.created_at ? new Date(c.created_at).toLocaleDateString() : 'N/A';

      const usedTd = document.createElement('td');
      usedTd.style.padding = '12px 8px';
      usedTd.textContent = c.used_at ? new Date(c.used_at).toLocaleDateString() : '-';

      tr.appendChild(codeTd);
      tr.appendChild(valTd);
      tr.appendChild(minTd);
      tr.appendChild(statusTd);
      tr.appendChild(createdTd);
      tr.appendChild(usedTd);

      tbody.appendChild(tr);
    });
  }
}

function setupCouponRedeemHandlers(headers) {
  const btn100 = document.getElementById('mgc-redeem-100-btn');
  const btn250 = document.getElementById('mgc-redeem-250-btn');
  const btn500 = document.getElementById('mgc-redeem-500-btn');
  const alertBox = document.getElementById('mgc-redeem-alert');

  if (btn100 && !btn100.dataset.hooked) {
    btn100.dataset.hooked = 'true';
    btn100.addEventListener('click', async () => {
      await handleRedeemRequest(100, headers, btn100, alertBox);
    });
  }

  if (btn250 && !btn250.dataset.hooked) {
    btn250.dataset.hooked = 'true';
    btn250.addEventListener('click', async () => {
      await handleRedeemRequest(250, headers, btn250, alertBox);
    });
  }

  if (btn500 && !btn500.dataset.hooked) {
    btn500.dataset.hooked = 'true';
    btn500.addEventListener('click', async () => {
      await handleRedeemRequest(500, headers, btn500, alertBox);
    });
  }
}

async function handleRedeemRequest(value, headers, btn, alertBox) {
  if (btn.disabled) return;

  const originalText = btn.textContent;
  btn.disabled = true;
  btn.textContent = 'Processing...';

  if (alertBox) {
    alertBox.style.display = 'none';
  }

  try {
    const res = await fetchJsonHelper(`${PROXY_BASE}/generate-coupon`, {
      method: 'POST',
      headers: {
        ...headers,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ coupon_value: value })
    });

    if (alertBox) {
      alertBox.className = '';
      alertBox.style.backgroundColor = '#ecfdf5'; // Light green
      alertBox.style.border = '1px solid #10b981';
      alertBox.style.color = '#065f46';
      alertBox.style.display = 'block';
      alertBox.textContent = res.message || `Your ₹${value} coupon has been generated successfully: ${res.coupon_code}`;
    }

    // Reload dashboard to get updated balance and coupon list
    setTimeout(() => {
      initializeDashboard();
    }, 1500);

  } catch (err) {
    console.error('Error redeeming coins:', err);
    if (alertBox) {
      alertBox.className = '';
      alertBox.style.backgroundColor = '#fef2f2'; // Light red
      alertBox.style.border = '1px solid #ef4444';
      alertBox.style.color = '#991b1b';
      alertBox.style.display = 'block';
      alertBox.textContent = err.message || 'Failed to redeem coins. Please try again.';
    }
    btn.disabled = false;
    btn.textContent = originalText;
  }
}

/**
 * Purchase Milestone Rewards - Fetch and Render (CHANGE 2)
 */
async function fetchAndRenderPurchaseMilestones(headers, prefetchedData = null) {
  const card = document.getElementById('mgc-purchase-milestones-card');
  const container = document.getElementById('mgc-milestone-stages-container');
  const countEl = document.getElementById('mgc-eligible-orders-count');

  if (!card || !container) return;

  try {
    let res = prefetchedData;
    if (!res) {
      const token = localStorage.getItem('mgc_session_token');
      res = await fetchJsonHelper(`${PROXY_BASE}/purchase-tier-rewards`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
    }

    if (!res.success || !res.enabled || !res.milestones || res.milestones.length === 0) {
      card.style.display = 'none';
      return;
    }

    card.style.display = 'flex';
    if (countEl) countEl.textContent = res.eligibleOrderCount || 0;

    container.innerHTML = '';
    res.milestones.forEach(m => {
      const isClaimed = m.status === 'claimed';
      const isAvailable = m.status === 'available';
      const isLocked = m.status === 'locked';
      const progressPct = Math.min(100, Math.round(((res.eligibleOrderCount || 0) / m.targetOrderCount) * 100));

      let statusIcon = 'lock';
      let statusColor = '#1e73e8';
      let iconColor = '#64748b';
      let cardBg = '#ffffff';
      let borderColor = '#e2edf8';
      let btnHtml = '';

      if (isClaimed) {
        statusIcon = 'check_circle';
        statusColor = '#00875a';
        iconColor = '#00875a';
        cardBg = '#f0fdf4';
        borderColor = '#dcfce7';
        btnHtml = `<span style="font-size:11px;font-weight:700;color:#00875a;background:#ffffff;border:1px solid #dcfce7;padding:4px 12px;border-radius:999px;display:inline-flex;align-items:center;gap:4px;">✓ Claimed</span>`;
      } else if (isAvailable) {
        statusIcon = 'lock_open';
        statusColor = '#1e73e8';
        iconColor = '#1e73e8';
        cardBg = '#ffffff';
        borderColor = '#e2edf8';
        btnHtml = `<button 
          onclick="claimMilestoneReward('${m.key}', this)" 
          style="font-size:12px;font-weight:700;color:#fff;background:linear-gradient(135deg,#4796ec,#1e73e8);border:none;padding:6px 16px;border-radius:999px;cursor:pointer;transition:all 0.2s;"
          onmouseover="this.style.opacity='0.9';" onmouseout="this.style.opacity='1';"
        >Claim ${m.rewardCoins} MGC</button>`;
      } else {
        iconColor = '#94a3b8';
        btnHtml = `<span style="font-size:11px;color:#64748b;">${res.eligibleOrderCount || 0}/${m.targetOrderCount} orders</span>`;
      }

      const stageNum = (m.key || '').replace('purchase_stage_', '') || '?';
      const stageEl = document.createElement('div');
      stageEl.style.cssText = `background:${cardBg};border:1px solid ${borderColor};border-radius:14px;padding:12px 16px;display:flex;flex-direction:column;gap:8px;`;
      stageEl.innerHTML = `
        <div style="display:flex;align-items:center;justify-content:space-between;">
          <div style="display:flex;align-items:center;gap:8px;">
            <span class="material-symbols-outlined" style="color:${iconColor};font-size:18px;font-variation-settings:'FILL' 1;">${statusIcon}</span>
            <span style="font-size:13px;font-weight:700;color:#1e293b;">Stage ${stageNum}: ${m.targetOrderCount} Orders</span>
          </div>
          <span style="font-size:13px;font-weight:800;color:${statusColor};">+${m.rewardCoins} MGC</span>
        </div>
        ${!isClaimed ? `
        <div style="background:#e2edf8;border-radius:999px;height:6px;overflow:hidden;">
          <div style="background:${isAvailable ? 'linear-gradient(90deg, #d97706, #b45309)' : '#1e73e8'};width:${progressPct}%;height:100%;border-radius:999px;transition:width 0.4s;"></div>
        </div>` : ''}
        <div style="display:flex;align-items:center;justify-content:flex-end;">${btnHtml}</div>
      `;
      container.appendChild(stageEl);
    });

  } catch (err) {
    console.warn('[MGC] Purchase milestone fetch failed:', err.message);
    if (card) card.style.display = 'none';
  }
}

window.claimMilestoneReward = async function (milestoneKey, btn) {
  if (!milestoneKey || !btn) return;
  const originalText = btn.textContent;
  btn.disabled = true;
  btn.textContent = 'Claiming...';
  btn.style.opacity = '0.7';

  try {
    const token = localStorage.getItem('mgc_session_token');
    const res = await fetchJsonHelper(`${PROXY_BASE}/purchase-tier-rewards/claim`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify({ milestoneKey })
    });

    btn.textContent = '✓ Claimed!';
    btn.style.background = 'linear-gradient(135deg,#059669,#047857)';
    btn.style.opacity = '1';

    // Refresh coins display
    if (res.newBalance !== undefined) {
      const coinEl = document.getElementById('mgc-coin-count');
      if (coinEl) coinEl.textContent = res.newBalance;
      const rupeeEl = document.getElementById('mgc-rupee-value');
      if (rupeeEl) rupeeEl.textContent = `₹${(res.newBalance * 0.25).toFixed(2)}`;
    }

    // Refresh milestones after 1.5s
    setTimeout(async () => {
      const headers = { 'Authorization': `Bearer ${token}` };
      await fetchAndRenderPurchaseMilestones(headers).catch(() => { });
    }, 1500);

  } catch (err) {
    btn.disabled = false;
    btn.textContent = originalText;
    btn.style.opacity = '1';
    alert(err.message || 'Failed to claim milestone reward.');
  }
};

function renderTransactionsList(transactions) {
  // Render bento list if container exists
  const listContainer = document.getElementById('mgc-transactions-list');
  if (listContainer) {
    listContainer.innerHTML = '';
    if (!transactions || transactions.length === 0) {
      listContainer.innerHTML = `
        <div class="text-center py-12 text-on-surface-variant/70 font-semibold bg-white border rounded-2xl">
          <span class="material-symbols-outlined text-4xl block mb-2 opacity-55">history</span>
          No transactions recorded yet.
        </div>
      `;
    } else {
      transactions.forEach(t => {
        const changeVal = parseInt(t.change_amount || 0, 10);
        const isCredit = changeVal >= 0;

        let icon = 'add_circle';
        let iconColorClass = 'text-tertiary bg-tertiary/10';
        const reasonLower = (t.reason || '').toLowerCase();

        if (reasonLower.includes('referral') && !reasonLower.includes('reversal')) {
          icon = 'group_add';
          iconColorClass = 'text-tertiary bg-tertiary/10';
        } else if (reasonLower.includes('redeem') || reasonLower.includes('coupon_redeem')) {
          icon = 'confirmation_number';
          iconColorClass = 'text-error bg-error/10';
        } else if (reasonLower.includes('order_reward') || reasonLower.includes('purchase_reward')) {
          icon = 'shopping_bag';
          iconColorClass = 'text-primary bg-primary/10';
        } else if (reasonLower.includes('reversal') || !isCredit) {
          icon = 'undo';
          iconColorClass = 'text-error bg-error/10';
        } else if (reasonLower.includes('manual_adjustment')) {
          icon = 'tune';
          iconColorClass = 'text-primary bg-primary/10';
        }

        const dateStr = t.created_at ? new Date(t.created_at).toLocaleDateString('en-IN', {
          day: 'numeric',
          month: 'short',
          year: 'numeric'
        }) : 'N/A';

        const item = document.createElement('div');
        item.className = 'bento-card p-4 flex justify-between items-center';
        item.innerHTML = `
          <div class="flex items-center gap-4">
            <div class="w-10 h-10 rounded-full ${iconColorClass} flex items-center justify-center">
              <span class="material-symbols-outlined">${icon}</span>
            </div>
            <div>
              <p class="font-body-lg text-body-lg text-on-surface font-semibold text-left">${t.description || 'Wallet Transaction'}</p>
              <p class="font-body-sm text-body-sm text-on-surface-variant text-left">${dateStr}</p>
            </div>
          </div>
          <span class="font-title-md text-title-md ${isCredit ? 'text-tertiary' : 'text-error'} font-bold">
            ${isCredit ? '+' : ''}${changeVal} MGC
          </span>
        `;
        listContainer.appendChild(item);
      });
    }
  }

  // Fallback: Populate table
  const tbody = document.getElementById('mgc-transactions-table-body');
  if (tbody) {
    tbody.innerHTML = '';
    if (!transactions || transactions.length === 0) {
      tbody.innerHTML = `
        <tr>
          <td colspan="5" style="padding: 20px 8px; text-align: center; color: #6d797d;">No transactions recorded yet.</td>
        </tr>
      `;
      return;
    }

    transactions.forEach(t => {
      const tr = document.createElement('tr');
      tr.style.borderBottom = '1px solid #E5E7EB';

      const dateTd = document.createElement('td');
      dateTd.style.padding = '12px 8px';
      dateTd.textContent = t.created_at ? new Date(t.created_at).toLocaleString() : 'N/A';

      const typeTd = document.createElement('td');
      typeTd.style.padding = '12px 8px';
      typeTd.style.fontWeight = '600';
      typeTd.style.fontSize = '12px';
      typeTd.style.textTransform = 'uppercase';
      typeTd.textContent = t.reason || '';

      const descTd = document.createElement('td');
      descTd.style.padding = '12px 8px';
      descTd.style.color = '#4b5563';
      descTd.textContent = t.description || '-';

      const amountTd = document.createElement('td');
      amountTd.style.padding = '12px 8px';
      amountTd.style.textAlign = 'right';
      amountTd.style.fontWeight = 'bold';

      const changeVal = parseInt(t.change_amount || 0, 10);
      if (changeVal >= 0) {
        amountTd.style.color = '#059669';
        amountTd.textContent = `+${changeVal} MGC`;
      } else {
        amountTd.style.color = '#dc2626';
        amountTd.textContent = `${changeVal} MGC`;
      }

      const balanceTd = document.createElement('td');
      balanceTd.style.padding = '12px 8px';
      balanceTd.style.textAlign = 'right';
      balanceTd.style.fontWeight = '600';
      balanceTd.textContent = `${t.balance_after || 0} MGC`;

      tr.appendChild(dateTd);
      tr.appendChild(typeTd);
      tr.appendChild(descTd);
      tr.appendChild(amountTd);
      tr.appendChild(balanceTd);

      tbody.appendChild(tr);
    });
  }
}

let currentOrders = [];
let isOrdersCollapsed = true;

function renderOrdersList(orders) {
  currentOrders = orders || [];
  const emptyState = document.getElementById('mgc-orders-empty');
  const listWrapper = document.getElementById('mgc-orders-list-wrapper');
  const toggleContainer = document.getElementById('mgc-orders-show-more-container');
  const toggleBtn = document.getElementById('mgc-orders-toggle-btn');

  if (!emptyState || !listWrapper) return;

  listWrapper.innerHTML = '';

  if (!currentOrders || currentOrders.length === 0) {
    emptyState.style.display = 'block';
    listWrapper.style.display = 'none';
    if (toggleContainer) toggleContainer.style.display = 'none';
    return;
  }

  emptyState.style.display = 'none';
  listWrapper.style.display = 'flex';

  const ordersToDisplay = isOrdersCollapsed ? currentOrders.slice(0, 2) : currentOrders;

  ordersToDisplay.forEach(order => {
    const orderDate = new Date(order.createdAt).toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });

    const orderEl = document.createElement('div');
    orderEl.className = 'bento-card p-6 flex flex-col gap-4 bg-white border border-outline-variant rounded-2xl';

    // Status mapping
    const payStatus = order.financialStatus ? order.financialStatus.toLowerCase() : 'unpaid';
    const fillStatus = order.fulfillmentStatus ? order.fulfillmentStatus.toLowerCase() : 'unfulfilled';

    let statusText = 'Pending';
    let statusClass = 'bg-primary/10 text-primary border-primary/20';
    if (fillStatus === 'fulfilled') {
      statusText = 'Delivered';
      statusClass = 'bg-tertiary-container/10 text-tertiary border-tertiary/20';
    } else if (payStatus === 'voided') {
      statusText = 'Voided';
      statusClass = 'bg-outline-variant text-outline border-outline-variant';
    }

    orderEl.innerHTML = `
      <div class="flex flex-col md:flex-row justify-between items-center gap-4 w-full">
        <div class="flex items-center gap-4 w-full md:w-auto">
          <div class="w-16 h-16 bg-surface-container rounded-lg flex items-center justify-center text-outline">
            <span class="material-symbols-outlined text-4xl">shopping_bag</span>
          </div>
          <div>
            <h4 class="font-title-md text-title-md text-on-surface font-semibold text-left">Order ${order.name}</h4>
            <p class="font-body-sm text-body-sm text-on-surface-variant text-left">Placed on ${orderDate} • ${order.items.length} item${order.items.length > 1 ? 's' : ''}</p>
          </div>
        </div>
        <div class="flex items-center gap-6 w-full md:w-auto justify-between md:justify-end">
          <span class="font-title-md text-title-md text-on-surface font-bold">₹${parseFloat(order.totalPrice).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
          <div class="flex items-center gap-3">
            <span class="px-3 py-1 ${statusClass} text-label-md font-label-md rounded-full border text-xs font-semibold">${statusText}</span>
            <button class="material-symbols-outlined text-primary hover:bg-primary/5 p-1 rounded-full transition-transform toggle-items-btn">keyboard_arrow_down</button>
          </div>
        </div>
      </div>
      <!-- Expanded items list (hidden by default) -->
      <div class="order-items-list hidden border-t border-outline-variant/30 pt-4 flex flex-col gap-4 mt-2">
        ${order.items.map(item => {
      const productLink = item.productHandle ? `/products/${item.productHandle}` : '#';
      const trackingLink = item.trackingUrl || order.trackingUrl || productLink;
      const trackingTitle = (item.trackingUrl || order.trackingUrl)
        ? `Track AWB Shipment (${item.trackingCompany || order.trackingCompany || 'Courier'})`
        : 'View product details';
      const imgHtml = item.imageUrl
        ? `<img src="${item.imageUrl}" style="width: 48px; height: 48px; border-radius: 6px; object-fit: cover; border: 1px solid #e2e8f0;" alt="${item.title}">`
        : `<div style="width: 48px; height: 48px; border-radius: 6px; background-color: #f1f5f9; border: 1px solid #e2e8f0; display: flex; align-items: center; justify-content: center; color: #94a3b8;"><span class="material-symbols-outlined" style="font-size: 20px;">image</span></div>`;

      return `
            <div class="flex flex-col sm:flex-row sm:items-center justify-between gap-3 py-3 border-b border-outline-variant/15 last:border-b-0 text-left">
              <div class="flex items-center gap-3.5 min-w-0">
                ${imgHtml}
                <div class="text-left min-w-0 flex-1">
                  <a href="${productLink}" style="font-size: 14px; font-weight: 600; color: #00677d; text-decoration: none;" target="_blank" class="hover:underline line-clamp-1">${item.title}</a>
                  <p style="font-size: 12px; color: #64748b; margin-top: 2px;">Qty: <span style="font-weight: 600; color: #334155;">${item.quantity}</span> × ₹${parseFloat(item.price).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</p>
                </div>
              </div>
              <div class="flex items-center justify-end flex-wrap gap-2.5 shrink-0">
                <div class="dispatch-proof-btn-container inline-flex items-center" data-order-id="${order.id}" data-order-name="${order.name || ''}" data-line-item-id="${item.lineItemId || ''}" data-sku="${item.sku || ''}"></div>
                ${trackingLink && trackingLink !== '#' ? `
                  <a href="${trackingLink}" class="inline-flex items-center gap-1.5 text-xs font-semibold text-primary bg-primary/5 hover:bg-primary/10 border border-primary/30 px-3 py-1.5 rounded-lg transition-all shadow-sm" target="_blank" title="${trackingTitle}">
                    <span>Track</span>
                    <span class="material-symbols-outlined" style="font-size: 13px;">open_in_new</span>
                  </a>
                ` : ''}
                ${(() => {
          let returnButtonHtml = '';
          if (item.returnRequestStatus) {
            const stat = item.returnRequestStatus;
            let displayStatus = 'Return Requested';
            let badgeClass = 'text-slate-600 bg-slate-100 border-slate-200';
            if (stat === 'pending_review') {
              displayStatus = 'Return: Pending Review';
              badgeClass = 'text-amber-700 bg-amber-50 border-amber-200';
            } else if (stat === 'approved') {
              displayStatus = 'Return: Approved';
              badgeClass = 'text-emerald-700 bg-emerald-50 border-emerald-200';
            } else if (stat === 'rejected') {
              displayStatus = 'Return: Rejected';
              badgeClass = 'text-rose-700 bg-rose-50 border-rose-200';
            } else if (stat === 'in_progress') {
              displayStatus = 'Return: In Progress';
              badgeClass = 'text-cyan-700 bg-cyan-50 border-cyan-200';
            } else if (stat === 'completed') {
              displayStatus = 'Return: Completed';
              badgeClass = 'text-emerald-800 bg-emerald-100 border-emerald-300';
            } else if (stat === 'cancelled_by_customer') {
              displayStatus = 'Return: Cancelled';
              badgeClass = 'text-slate-500 bg-slate-100 border-slate-200';
            }
            returnButtonHtml = `<span class="inline-flex items-center text-xs font-semibold px-2.5 py-1 rounded-full border ${badgeClass}">${displayStatus}</span>`;
          } else if (item.returnEligible) {
            returnButtonHtml = `
                      <button type="button" 
                        onclick="openReturnModal('${order.id}', '${item.lineItemId}', '${order.name}', '${order.createdAt}', '${escapeHtml(item.title)}', '${item.sku}', ${item.quantity}, '${item.imageUrl || ''}')"
                        class="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-700 bg-white hover:bg-slate-50 border border-slate-300 px-3 py-1.5 rounded-lg transition-all shadow-sm cursor-pointer">
                        <span class="material-symbols-outlined text-slate-500" style="font-size: 15px;">assignment_return</span>
                        <span>Return Request</span>
                      </button>
                    `;
          } else {
            returnButtonHtml = '';
          }
          return returnButtonHtml;
        })()}
              </div>
            </div>
          `;
    }).join('')}
      </div>
    `;

    // Hook toggle event on the order card
    const toggleBtn = orderEl.querySelector('.toggle-items-btn');
    const itemsList = orderEl.querySelector('.order-items-list');
    toggleBtn.addEventListener('click', (e) => {
      e.stopPropagation();
      const isHidden = itemsList.classList.contains('hidden');
      if (isHidden) {
        itemsList.classList.remove('hidden');
        toggleBtn.textContent = 'keyboard_arrow_up';
      } else {
        itemsList.classList.add('hidden');
        toggleBtn.textContent = 'keyboard_arrow_down';
      }
    });

    listWrapper.appendChild(orderEl);
  });

  // Handle Show More / Show Less button visibility and behavior
  if (toggleContainer && toggleBtn) {
    if (currentOrders.length > 2) {
      toggleContainer.style.display = 'block';
      if (isOrdersCollapsed) {
        toggleBtn.innerHTML = `
          <span>Show More Orders (${currentOrders.length - 2} more)</span>
          <span class="material-symbols-outlined" style="font-size: 18px;">expand_more</span>
        `;
      } else {
        toggleBtn.innerHTML = `
          <span>Show Less Orders</span>
          <span class="material-symbols-outlined" style="font-size: 18px;">expand_less</span>
        `;
      }
    } else {
      toggleContainer.style.display = 'none';
    }
  }

  // Asynchronously fetch and attach dispatch proofs for displayed orders
  fetchAndAttachDispatchProofs(ordersToDisplay);
}

// Dispatch & Packing Proofs Integration
window.openDispatchProofModal = function(imageUrl, title) {
  let modal = document.getElementById('mgc-dispatch-proof-modal');
  if (!modal) {
    modal = document.createElement('div');
    modal.id = 'mgc-dispatch-proof-modal';
    modal.className = 'fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-950/60 backdrop-blur-xs';
    modal.style.display = 'none';
    modal.innerHTML = `
      <div class="bg-white rounded-2xl border border-slate-200 shadow-2xl max-w-lg w-full overflow-hidden flex flex-col">
        <div class="p-4 border-b border-slate-100 flex items-center justify-between bg-slate-50">
          <h3 class="font-bold text-slate-800 text-sm flex items-center gap-2">
            <span class="material-symbols-outlined text-emerald-600 font-bold" style="font-size: 20px;">verified</span>
            <span>Dispatch &amp; Packing Proof</span>
          </h3>
          <button type="button" onclick="closeDispatchProofModal()" class="text-slate-400 hover:text-slate-600 p-1 rounded-lg">
            <span class="material-symbols-outlined" style="font-size: 20px;">close</span>
          </button>
        </div>
        <div class="p-6 text-center space-y-4">
          <div id="mgc-dp-modal-item-title" class="font-semibold text-xs text-slate-700"></div>
          <div class="relative group rounded-xl overflow-hidden border border-slate-200 shadow-sm bg-slate-50">
            <a id="mgc-dp-modal-img-link" href="#" target="_blank" title="Click to view high resolution image">
              <img id="mgc-dp-modal-img" src="" alt="Dispatch Proof" style="max-height: 380px;" class="w-full object-contain mx-auto">
            </a>
          </div>
          <p class="text-[11px] text-slate-500 italic">
            Official packing snapshot taken at warehouse prior to courier dispatch.
          </p>
        </div>
      </div>
    `;
    document.body.appendChild(modal);
  }

  const titleEl = document.getElementById('mgc-dp-modal-item-title');
  const imgEl = document.getElementById('mgc-dp-modal-img');
  const linkEl = document.getElementById('mgc-dp-modal-img-link');

  if (titleEl) titleEl.textContent = title || 'Dispatch Proof';
  if (imgEl) imgEl.src = imageUrl;
  if (linkEl) linkEl.href = imageUrl;

  modal.style.display = 'flex';
};

window.closeDispatchProofModal = function() {
  const modal = document.getElementById('mgc-dispatch-proof-modal');
  if (modal) modal.style.display = 'none';
};

async function fetchAndAttachDispatchProofs(orders) {
  if (!orders || orders.length === 0) return;

  orders.forEach(async (order) => {
    const rawOrderId = String(order.id || '').replace(/^gid:\/\/shopify\/Order\//, '').trim();
    const orderNameClean = String(order.name || '').replace(/^#/, '').trim();
    const idsToTry = Array.from(new Set([rawOrderId, orderNameClean].filter(Boolean)));

    let proofs = [];
    for (const queryId of idsToTry) {
      try {
        let res = await fetch(`${PROXY_BASE}/api/customer/orders/${encodeURIComponent(queryId)}/dispatch-proofs`);
        if (!res.ok) {
          res = await fetch(`/apps/proxy/api/customer/orders/${encodeURIComponent(queryId)}/dispatch-proofs`);
        }
        if (!res.ok) {
          res = await fetch(`/api/customer/orders/${encodeURIComponent(queryId)}/dispatch-proofs`);
        }
        if (res.ok) {
          const data = await res.json();
          if (data && data.success && Array.isArray(data.proofs) && data.proofs.length > 0) {
            proofs = data.proofs;
            break;
          }
        }
      } catch (err) {
        console.warn('Dispatch proof fetch notice:', err);
      }
    }

    if (proofs && proofs.length > 0) {
      const proofsMap = {};
      proofs.forEach(p => {
        if (p.line_item_id) proofsMap[String(p.line_item_id)] = p;
        if (p.sku) proofsMap[String(p.sku)] = p;
      });

      const allContainers = document.querySelectorAll('.dispatch-proof-btn-container');
      allContainers.forEach(container => {
        const cOrderId = container.getAttribute('data-order-id') || '';
        const cOrderName = container.getAttribute('data-order-name') || '';

        const matchesOrder =
          cOrderId === String(order.id) ||
          cOrderId === rawOrderId ||
          cOrderName === String(order.name) ||
          cOrderName === orderNameClean ||
          cOrderName === `#${orderNameClean}`;

        if (matchesOrder) {
          const lineItemId = String(container.getAttribute('data-line-item-id') || '');
          const sku = String(container.getAttribute('data-sku') || '');
          const proof = proofsMap[lineItemId] || proofsMap[sku] || proofs[0];

          if (proof && proof.image_url) {
            const cleanUrl = proof.image_url.replace(/'/g, "\\'");
            const cleanTitle = (proof.line_item_title || 'Dispatch Proof').replace(/'/g, "\\'");

            container.innerHTML = `
              <button type="button"
                onclick="openDispatchProofModal('${cleanUrl}', '${cleanTitle}')"
                class="inline-flex items-center gap-1.5 text-xs font-semibold text-emerald-700 bg-emerald-50 hover:bg-emerald-100 border border-emerald-300 px-3 py-1.5 rounded-lg transition-all shadow-sm cursor-pointer"
                title="View Dispatch & Packing Proof">
                <span class="material-symbols-outlined text-emerald-600" style="font-size: 15px;">verified</span>
                <span>View Proof</span>
              </button>
            `;
          }
        }
      });
    }
  });
}

// Profile Modal Controllers
window.switchToEditMode = function () {
  const viewMode = document.getElementById('profile-view-mode');
  const editForm = document.getElementById('mgc-profile-form');
  if (viewMode) viewMode.style.display = 'none';
  if (editForm) editForm.style.display = 'flex';
};

window.switchToViewMode = function () {
  const viewMode = document.getElementById('profile-view-mode');
  const editForm = document.getElementById('mgc-profile-form');
  if (viewMode) viewMode.style.display = 'flex';
  if (editForm) editForm.style.display = 'none';
};

window.openProfileModal = function () {
  const modal = document.getElementById('mgc-profile-modal');
  const alertDiv = document.getElementById('mgc-profile-alert');
  if (alertDiv) alertDiv.style.display = 'none';

  if (modal) {
    modal.style.display = 'flex';

    // Populate fields from globally cached data
    const data = window.mgcCustomerData || {};

    // 1. Populate VIEW Mode elements
    const viewEmail = document.getElementById('view-profile-email');
    const viewPhone = document.getElementById('view-profile-phone');
    const viewName = document.getElementById('view-profile-name');
    const viewAddress1 = document.getElementById('view-profile-address1');
    const viewCity = document.getElementById('view-profile-city');
    const viewProvince = document.getElementById('view-profile-province');
    const viewZip = document.getElementById('view-profile-zip');

    if (viewEmail) viewEmail.textContent = data.email || 'Not added';
    if (viewPhone) viewPhone.textContent = data.phone || 'Not added';
    if (viewName) viewName.textContent = data.name || 'Not added';

    const hasAddress = !!data.address1;
    const addressCard = document.getElementById('view-profile-address-card');
    const addressEmpty = document.getElementById('view-profile-address-empty');

    if (hasAddress) {
      if (viewAddress1) viewAddress1.textContent = data.address1 || '';
      if (viewCity) viewCity.textContent = data.city_only || data.city || '';
      if (viewProvince) viewProvince.textContent = data.province || 'N/A';
      if (viewZip) viewZip.textContent = data.zip || 'N/A';
      if (addressCard) addressCard.style.display = 'flex';
      if (addressEmpty) addressEmpty.style.display = 'none';
    } else {
      if (addressCard) addressCard.style.display = 'none';
      if (addressEmpty) addressEmpty.style.display = 'flex';
    }

    // 2. Populate EDIT Mode input fields
    const nameInput = document.getElementById('profile-name');
    const address1Input = document.getElementById('profile-address1');
    const cityInput = document.getElementById('profile-city');
    const provinceInput = document.getElementById('profile-province');
    const zipInput = document.getElementById('profile-zip');

    if (nameInput) nameInput.value = data.name || '';
    if (address1Input) address1Input.value = data.address1 || '';
    if (cityInput) cityInput.value = data.city_only || data.city || '';
    if (provinceInput) provinceInput.value = data.province || '';
    if (zipInput) zipInput.value = data.zip || '';

    // Always start in VIEW mode
    window.switchToViewMode();
  }
};

window.closeProfileModal = function () {
  const modal = document.getElementById('mgc-profile-modal');
  if (modal) {
    modal.style.display = 'none';
  }
};

// Bind profile trigger click & submit handlers
document.addEventListener('DOMContentLoaded', () => {
  const trigger = document.getElementById('mgc-profile-trigger');
  if (trigger) {
    trigger.addEventListener('click', () => {
      window.openProfileModal();
    });
  }

  const form = document.getElementById('mgc-profile-form');
  if (form) {
    form.addEventListener('submit', async function (e) {
      e.preventDefault();
      const name = document.getElementById('profile-name').value.trim();
      const address1 = document.getElementById('profile-address1').value.trim();
      const city = document.getElementById('profile-city').value.trim();
      const province = document.getElementById('profile-province').value.trim();
      const zip = document.getElementById('profile-zip').value.trim();
      const submitBtn = document.getElementById('profile-submit-btn');
      const alertDiv = document.getElementById('mgc-profile-alert');

      if (!name || !address1 || !city || !province || !zip) return;

      submitBtn.disabled = true;
      submitBtn.innerText = 'Saving...';
      alertDiv.style.display = 'none';

      const token = localStorage.getItem('mgc_session_token');
      const headers = { 'Content-Type': 'application/json' };
      if (token) headers['Authorization'] = `Bearer ${token}`;

      try {
        const data = await fetchJsonHelper(`${PROXY_BASE}/update-profile`, {
          method: 'POST',
          headers: headers,
          body: JSON.stringify({ name, address1, city, province, zip })
        });

        alertDiv.textContent = 'Profile updated successfully!';
        alertDiv.style.backgroundColor = '#ecfdf5';
        alertDiv.style.color = '#047857';
        alertDiv.style.border = '1px solid #a7f3d0';
        alertDiv.style.display = 'block';

        // Update local cache
        if (window.mgcCustomerData) {
          window.mgcCustomerData.name = name;
          window.mgcCustomerData.address1 = address1;
          window.mgcCustomerData.city_only = city;
          window.mgcCustomerData.province = province;
          window.mgcCustomerData.zip = zip;
          window.mgcCustomerData.city = address1 ? `${address1}, ${city}` : city;
        }

        // Update header UI
        const nameEl = document.getElementById('mgc-user-name');
        const cityEl = document.getElementById('mgc-user-city');
        if (nameEl) nameEl.textContent = name;
        if (cityEl) cityEl.textContent = address1 ? `${address1}, ${city}` : city;

        // Re-populate View cards with fresh data
        window.openProfileModal();

        // Hide modal after delay
        setTimeout(() => {
          window.closeProfileModal();
        }, 1500);

      } catch (err) {
        alertDiv.textContent = err.message || 'Failed to update profile.';
        alertDiv.style.backgroundColor = '#fef2f2';
        alertDiv.style.color = '#b91c1c';
        alertDiv.style.border = '1px solid #fecaca';
        alertDiv.style.display = 'block';
      } finally {
        submitBtn.disabled = false;
        submitBtn.innerText = 'Save Changes';
      }
    });
  }
});

window.openReturnModal = function (orderId, itemId, orderName, orderDate, itemTitle, itemSku, itemQty, itemImg) {
  const alertEl = document.getElementById('mgc-return-alert');
  if (alertEl) {
    alertEl.style.display = 'none';
    alertEl.className = 'hidden p-3 rounded-xl text-center font-semibold text-xs mt-4';
    alertEl.textContent = '';
  }

  document.getElementById('return-order-id').value = orderId;
  document.getElementById('return-line-item-id').value = itemId;

  document.getElementById('return-order-name').textContent = orderName || 'N/A';
  const formattedDate = new Date(orderDate).toLocaleDateString('en-IN', {
    day: 'numeric', month: 'short', year: 'numeric'
  });
  document.getElementById('return-order-date').textContent = formattedDate || 'N/A';
  document.getElementById('return-item-title').textContent = itemTitle || 'N/A';
  document.getElementById('return-item-sku').textContent = itemSku ? `SKU: ${itemSku}` : 'SKU: N/A';

  const qtyInput = document.getElementById('return-qty');
  qtyInput.value = '1';
  qtyInput.max = itemQty;
  qtyInput.min = '1';
  document.getElementById('return-qty-hint').textContent = `Ordered: ${itemQty}`;

  const imgContainer = document.getElementById('return-item-image-container');
  if (itemImg) {
    imgContainer.innerHTML = `<img src="${itemImg}" style="width: 48px; height: 48px; border-radius: 6px; object-fit: cover; border: 1px solid #e2e8f0;" alt="${itemTitle}">`;
  } else {
    imgContainer.innerHTML = `<div style="width: 48px; height: 48px; border-radius: 6px; background-color: #f1f5f9; border: 1px solid #e2e8f0; display: flex; align-items: center; justify-content: center; color: #94a3b8;"><span class="material-symbols-outlined" style="font-size: 20px;">image</span></div>`;
  }

  document.getElementById('return-reason').value = '';
  document.getElementById('return-policy-ack').checked = false;

  const modal = document.getElementById('mgc-return-modal');
  modal.style.display = 'flex';
};

window.closeReturnModal = function () {
  const modal = document.getElementById('mgc-return-modal');
  modal.style.display = 'none';
};

window.submitReturnRequest = async function (event) {
  event.preventDefault();

  const alertEl = document.getElementById('mgc-return-alert');
  const submitBtn = document.getElementById('return-submit-btn');

  alertEl.style.display = 'none';
  alertEl.className = 'hidden p-3 rounded-xl text-center font-semibold text-xs mt-4';
  alertEl.textContent = '';

  const orderId = document.getElementById('return-order-id').value;
  const lineItemId = document.getElementById('return-line-item-id').value;
  const qty = parseInt(document.getElementById('return-qty').value, 10);
  const reason = document.getElementById('return-reason').value.trim();
  const ack = document.getElementById('return-policy-ack').checked;

  const maxQty = parseInt(document.getElementById('return-qty').max, 10);

  if (!qty || isNaN(qty) || qty < 1 || qty > maxQty) {
    alertEl.style.display = 'block';
    alertEl.className = 'p-3 rounded-xl text-center font-semibold text-xs mt-4 bg-red-50 text-red-700 border border-red-200';
    alertEl.style.color = '#b91c1c';
    alertEl.textContent = `Please enter a valid quantity between 1 and ${maxQty}.`;
    return;
  }

  if (reason.length < 10) {
    alertEl.style.display = 'block';
    alertEl.className = 'p-3 rounded-xl text-center font-semibold text-xs mt-4 bg-red-50 text-red-700 border border-red-200';
    alertEl.style.color = '#b91c1c';
    alertEl.textContent = 'Reason for return must be at least 10 characters long.';
    return;
  }

  if (reason.length > 1000) {
    alertEl.style.display = 'block';
    alertEl.className = 'p-3 rounded-xl text-center font-semibold text-xs mt-4 bg-red-50 text-red-700 border border-red-200';
    alertEl.style.color = '#b91c1c';
    alertEl.textContent = 'Reason for return cannot exceed 1000 characters.';
    return;
  }

  if (!ack) {
    alertEl.style.display = 'block';
    alertEl.className = 'p-3 rounded-xl text-center font-semibold text-xs mt-4 bg-red-50 text-red-700 border border-red-200';
    alertEl.style.color = '#b91c1c';
    alertEl.textContent = 'You must acknowledge the Return & Refund Policy.';
    return;
  }

  submitBtn.disabled = true;
  submitBtn.style.opacity = '0.7';
  submitBtn.textContent = 'Submitting...';

  try {
    const customerToken = localStorage.getItem('mgc_session_token');
    const payload = {
      customerToken,
      orderId,
      lineItemId,
      quantityToReturn: qty,
      reason,
      policyNoteAcknowledged: ack
    };

    const res = await fetchJsonHelper(`${PROXY_BASE}/returns/request`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${customerToken}`
      },
      body: JSON.stringify(payload)
    });

    alertEl.style.display = 'block';
    alertEl.className = 'p-3 rounded-xl text-center font-semibold text-xs mt-4 bg-teal-50 text-teal-800 border border-teal-200';
    alertEl.style.color = '#0f766e';
    alertEl.textContent = `Success! Return request submitted (Ref: ${res.returnRequestNumber}).`;

    setTimeout(async () => {
      window.closeReturnModal();
      location.reload();
    }, 2000);

  } catch (err) {
    alertEl.style.display = 'block';
    alertEl.className = 'p-3 rounded-xl text-center font-semibold text-xs mt-4 bg-red-50 text-red-700 border border-red-200';
    alertEl.style.color = '#b91c1c';
    alertEl.textContent = err.message || 'Submission failed. Please try again.';

    submitBtn.disabled = false;
    submitBtn.style.opacity = '1';
    submitBtn.textContent = 'Submit Request';
  }
};

